import { has } from '@formkit/utils';

/**
 * Given a string, convert it to sentence case.
 * @param item - The string to sentence case
 * @returns
 * @public
 */
function sentence(str) {
    return str[0].toUpperCase() + str.substr(1);
}
/**
 * Creates an oxford-comma separated list of items.
 * @param args - items to list out
 * @param conjunction - in: x, y, and z "and" is the conjunction to use
 * @returns
 * @public
 */
function list(items, conjunction = 'or') {
    return items.reduce((oxford, item, index) => {
        oxford += item;
        if (index <= items.length - 2 && items.length > 2) {
            oxford += ', ';
        }
        if (index === items.length - 2) {
            oxford += `${items.length === 2 ? ' ' : ''}${conjunction} `;
        }
        return oxford;
    }, '');
}
/**
 * Given a string or a date return a nice human-readable version.
 * @param date - A string or a date.
 * @public
 */
function date(date) {
    const dateTime = typeof date === 'string' ? new Date(Date.parse(date)) : date;
    if (!(dateTime instanceof Date)) {
        return '(unknown)';
    }
    return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
    }).format(dateTime);
}
/**
 * Orders two variables smallest to largest.
 * @param first - first argument
 * @param second - Second argument
 * @returns
 * @public
 */
function order(first, second) {
    return Number(first) >= Number(second) ? [second, first] : [first, second];
}

/**
 * Here we can import additional helper functions to assist in formatting our
 * language. Feel free to add additional helper methods to libs/formats if it
 * assists in creating good validation messages for your locale.
 */
/**
 * Standard language for interface features.
 * @public
 */
const ui$c = {
    /**
     * Shown when a button to remove items is visible.
     */
    remove: 'Entfernen',
    /**
     * Shown when there are multiple items to remove at the same time.
     */
    removeAll: 'Alles entfernen',
    /**
     * Shown when all fields are not filled out correctly.
     */
    incomplete: 'Entschuldigung, nicht alle Felder wurden korrekt ausgefüllt.',
    /**
     * Shown in a button inside a form to submit the form.
     */
    submit: 'Senden',
    /**
     * Shown when no files are selected.
     */
    noFiles: 'Keine Datei ausgewählt',
};
/**
 * These are all the possible strings that pertain to validation messages.
 * @public
 */
const validation$c = {
    /**
     * The value is not an accepted value.
     * @see {@link https://docs.formkit.com/essentials/validation#accepted}
     */
    accepted({ name }) {
        /* <i18n case="Shown when the user-provided value is not a valid 'accepted' value."> */
        return `Bitte ${name} akzeptieren.`;
        /* </i18n> */
    },
    /**
     * The date is not after
     * @see {@link https://docs.formkit.com/essentials/validation#date-after}
     */
    date_after({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date is not after the date supplied to the rule."> */
            return `${sentence(name)} muss nach dem ${date(args[0])} liegen.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided date is not after today's date, since no date was supplied to the rule."> */
        return `${sentence(name)} muss in der Zukunft liegen.`;
        /* </i18n> */
    },
    /**
     * The value is not a letter.
     * @see {@link https://docs.formkit.com/essentials/validation#alpha}
     */
    alpha({ name }) {
        /* <i18n case="Shown when the user-provided value contains non-alphabetical characters."> */
        return `${sentence(name)} darf nur Buchstaben enthalten.`;
        /* </i18n> */
    },
    /**
     * The value is not alphanumeric
     * @see {@link https://docs.formkit.com/essentials/validation#alphanumeric}
     */
    alphanumeric({ name }) {
        /* <i18n case="Shown when the user-provided value contains non-alphanumeric characters."> */
        return `${sentence(name)} darf nur Buchstaben und Zahlen enthalten.`;
        /* </i18n> */
    },
    /**
     * The date is not before
     * @see {@link https://docs.formkit.com/essentials/validation#date-before}
     */
    date_before({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date is not before the date supplied to the rule."> */
            return `${sentence(name)} muss vor dem ${date(args[0])} liegen.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided date is not before today's date, since no date was supplied to the rule."> */
        return `${sentence(name)} muss in der Vergangenheit liegen.`;
        /* </i18n> */
    },
    /**
     * The value is not between two numbers
     * @see {@link https://docs.formkit.com/essentials/validation#between}
     */
    between({ name, args }) {
        if (isNaN(args[0]) || isNaN(args[1])) {
            /* <i18n case="Shown when any of the arguments supplied to the rule were not a number."> */
            return `Dieses Feld wurde falsch konfiguriert und kann nicht übermittelt werden.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided value is not between two numbers."> */
        return `${sentence(name)} muss zwischen ${args[0]} und ${args[1]} sein.`;
        /* </i18n> */
    },
    /**
     * The confirmation field does not match
     * @see {@link https://docs.formkit.com/essentials/validation#confirm}
     */
    confirm({ name }) {
        /* <i18n case="Shown when the user-provided value does not equal the value of the matched input."> */
        return `${sentence(name)} stimmt nicht überein.`;
        /* </i18n> */
    },
    /**
     * The value is not a valid date
     * @see {@link https://docs.formkit.com/essentials/validation#date-format}
     */
    date_format({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date does not satisfy the date format supplied to the rule."> */
            return `${sentence(name)} ist kein gültiges Datum im Format ${args[0]}.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when no date argument was supplied to the rule."> */
        return 'Dieses Feld wurde falsch konfiguriert und kann nicht übermittelt werden.';
        /* </i18n> */
    },
    /**
     * Is not within expected date range
     * @see {@link https://docs.formkit.com/essentials/validation#date-between}
     */
    date_between({ name, args }) {
        /* <i18n case="Shown when the user-provided date is not between the start and end dates supplied to the rule. "> */
        return `${sentence(name)} muss zwischen ${date(args[0])} und ${date(args[1])} liegen.`;
        /* </i18n> */
    },
    /**
     * Shown when the user-provided value is not a valid email address.
     * @see {@link https://docs.formkit.com/essentials/validation#email}
     */
    email: 'E-Mail Adresse ist ungültig.',
    /**
     * Does not end with the specified value
     * @see {@link https://docs.formkit.com/essentials/validation#ends-with}
     */
    ends_with({ name, args }) {
        /* <i18n case="Shown when the user-provided value does not end with the substring supplied to the rule."> */
        return `${sentence(name)} endet nicht mit ${list(args)}.`;
        /* </i18n> */
    },
    /**
     * Is not an allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#is}
     */
    is({ name }) {
        /* <i18n case="Shown when the user-provided value is not one of the values supplied to the rule."> */
        return `${sentence(name)} enthält einen ungültigen Wert.`;
        /* </i18n> */
    },
    /**
     * Does not match specified length
     * @see {@link https://docs.formkit.com/essentials/validation#length}
     */
    length({ name, args: [first = 0, second = Infinity] }) {
        const min = first <= second ? first : second;
        const max = second >= first ? second : first;
        if (min == 1 && max === Infinity) {
            /* <i18n case="Shown when the length of the user-provided value is not at least one character."> */
            return `${sentence(name)} muss mindestens ein Zeichen enthalten.`;
            /* </i18n> */
        }
        if (min == 0 && max) {
            /* <i18n case="Shown when first argument supplied to the rule is 0, and the user-provided value is longer than the max (the 2nd argument) supplied to the rule."> */
            return `${sentence(name)} darf maximal ${max} Zeichen enthalten.`;
            /* </i18n> */
        }
        if (min && max === Infinity) {
            /* <i18n case="Shown when the length of the user-provided value is less than the minimum supplied to the rule and there is no maximum supplied to the rule."> */
            return `${sentence(name)} muss mindestens ${min} Zeichen enthalten.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the length of the user-provided value is between the two lengths supplied to the rule."> */
        return `${sentence(name)} muss zwischen ${min} und ${max} Zeichen enthalten.`;
        /* </i18n> */
    },
    /**
     * Value is not a match
     * @see {@link https://docs.formkit.com/essentials/validation#matches}
     */
    matches({ name }) {
        /* <i18n case="Shown when the user-provided value does not match any of the values or RegExp patterns supplied to the rule. "> */
        return `${sentence(name)} enthält einen ungültigen Wert.`;
        /* </i18n> */
    },
    /**
     * Exceeds maximum allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#max}
     */
    max({ name, node: { value }, args }) {
        if (Array.isArray(value)) {
            /* <i18n case="Shown when the length of the array of user-provided values is longer than the max supplied to the rule."> */
            return `Darf maximal ${args[0]} ${name} haben.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided value is greater than the maximum number supplied to the rule."> */
        return `${sentence(name)} darf maximal ${args[0]} sein.`;
        /* </i18n> */
    },
    /**
     * The (field-level) value does not match specified mime type
     * @see {@link https://docs.formkit.com/essentials/validation#mime}
     */
    mime({ name, args }) {
        if (!args[0]) {
            /* <i18n case="Shown when no file formats were supplied to the rule."> */
            return 'Keine Dateiformate konfiguriert.';
            /* </i18n> */
        }
        /* <i18n case="Shown when the mime type of user-provided file does not match any mime types supplied to the rule."> */
        return `${sentence(name)} muss vom Typ ${args[0]} sein.`;
        /* </i18n> */
    },
    /**
     * Does not fulfill minimum allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#min}
     */
    min({ name, node: { value }, args }) {
        if (Array.isArray(value)) {
            /* <i18n case="Shown when the length of the array of user-provided values is shorter than the min supplied to the rule."> */
            return `Mindestens ${args[0]} ${name} erforderlich.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided value is less than the minimum number supplied to the rule."> */
        return `${sentence(name)} muss mindestens ${args[0]} sein.`;
        /* </i18n> */
    },
    /**
     * Is not an allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#not}
     */
    not({ name, node: { value } }) {
        /* <i18n case="Shown when the user-provided value matches one of the values supplied to (and thus disallowed by) the rule."> */
        return `“${value}” ist kein gültiger Wert für ${name}.`;
        /* </i18n> */
    },
    /**
     *  Is not a number
     * @see {@link https://docs.formkit.com/essentials/validation#number}
     */
    number({ name }) {
        /* <i18n case="Shown when the user-provided value is not a number."> */
        return `${sentence(name)} muss eine Zahl sein.`;
        /* </i18n> */
    },
    /**
     * Required field.
     * @see {@link https://docs.formkit.com/essentials/validation#required}
     */
    required({ name }) {
        /* <i18n case="Shown when a user does not provide a value to a required input."> */
        return `${sentence(name)} ist erforderlich.`;
        /* </i18n> */
    },
    /**
     * Does not start with specified value
     * @see {@link https://docs.formkit.com/essentials/validation#starts-with}
     */
    starts_with({ name, args }) {
        /* <i18n case="Shown when the user-provided value does not start with the substring supplied to the rule."> */
        return `${sentence(name)} beginnt nicht mit ${list(args)}.`;
        /* </i18n> */
    },
    /**
     * Is not a url
     * @see {@link https://docs.formkit.com/essentials/validation#url}
     */
    url() {
        /* <i18n case="Shown when the user-provided value is not a valid url."> */
        return `URL ist ungültig.`;
        /* </i18n> */
    },
};

var de = /*#__PURE__*/Object.freeze({
    __proto__: null,
    ui: ui$c,
    validation: validation$c
});

/**
 * Here we can import additional helper functions to assist in formatting our
 * language. Feel free to add additional helper methods to libs/formats if it
 * assists in creating good validation messages for your locale.
 */
/**
 * Standard language for interface features.
 * @public
 */
const ui$b = {
    /**
     * Shown when a button to remove items is visible.
     */
    remove: 'Remove',
    /**
     * Shown when there are multiple items to remove at the same time.
     */
    removeAll: 'Remove all',
    /**
     * Shown when all fields are not filled out correctly.
     */
    incomplete: 'Sorry, not all fields are filled out correctly.',
    /**
     * Shown in a button inside a form to submit the form.
     */
    submit: 'Submit',
    /**
     * Shown when no files are selected.
     */
    noFiles: 'No file chosen',
};
/**
 * These are all the possible strings that pertain to validation messages.
 * @public
 */
const validation$b = {
    /**
     * The value is not an accepted value.
     * @see {@link https://docs.formkit.com/essentials/validation#accepted}
     */
    accepted({ name }) {
        /* <i18n case="Shown when the user-provided value is not a valid 'accepted' value."> */
        return `Please accept the ${name}.`;
        /* </i18n> */
    },
    /**
     * The date is not after
     * @see {@link https://docs.formkit.com/essentials/validation#date-after}
     */
    date_after({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date is not after the date supplied to the rule."> */
            return `${sentence(name)} must be after ${date(args[0])}.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided date is not after today's date, since no date was supplied to the rule."> */
        return `${sentence(name)} must be in the future.`;
        /* </i18n> */
    },
    /**
     * The value is not a letter.
     * @see {@link https://docs.formkit.com/essentials/validation#alpha}
     */
    alpha({ name }) {
        /* <i18n case="Shown when the user-provided value contains non-alphabetical characters."> */
        return `${sentence(name)} can only contain alphabetical characters.`;
        /* </i18n> */
    },
    /**
     * The value is not alphanumeric
     * @see {@link https://docs.formkit.com/essentials/validation#alphanumeric}
     */
    alphanumeric({ name }) {
        /* <i18n case="Shown when the user-provided value contains non-alphanumeric characters."> */
        return `${sentence(name)} can only contain letters and numbers.`;
        /* </i18n> */
    },
    /**
     * The date is not before
     * @see {@link https://docs.formkit.com/essentials/validation#date-before}
     */
    date_before({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date is not before the date supplied to the rule."> */
            return `${sentence(name)} must be before ${date(args[0])}.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided date is not before today's date, since no date was supplied to the rule."> */
        return `${sentence(name)} must be in the past.`;
        /* </i18n> */
    },
    /**
     * The value is not between two numbers
     * @see {@link https://docs.formkit.com/essentials/validation#between}
     */
    between({ name, args }) {
        if (isNaN(args[0]) || isNaN(args[1])) {
            /* <i18n case="Shown when any of the arguments supplied to the rule were not a number."> */
            return `This field was configured incorrectly and can’t be submitted.`;
            /* </i18n> */
        }
        const [a, b] = order(args[0], args[1]);
        /* <i18n case="Shown when the user-provided value is not between two numbers."> */
        return `${sentence(name)} must be between ${a} and ${b}.`;
        /* </i18n> */
    },
    /**
     * The confirmation field does not match
     * @see {@link https://docs.formkit.com/essentials/validation#confirm}
     */
    confirm({ name }) {
        /* <i18n case="Shown when the user-provided value does not equal the value of the matched input."> */
        return `${sentence(name)} does not match.`;
        /* </i18n> */
    },
    /**
     * The value is not a valid date
     * @see {@link https://docs.formkit.com/essentials/validation#date-format}
     */
    date_format({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date does not satisfy the date format supplied to the rule."> */
            return `${sentence(name)} is not a valid date, please use the format ${args[0]}`;
            /* </i18n> */
        }
        /* <i18n case="Shown when no date argument was supplied to the rule."> */
        return 'This field was configured incorrectly and can’t be submitted';
        /* </i18n> */
    },
    /**
     * Is not within expected date range
     * @see {@link https://docs.formkit.com/essentials/validation#date-between}
     */
    date_between({ name, args }) {
        /* <i18n case="Shown when the user-provided date is not between the start and end dates supplied to the rule. "> */
        return `${sentence(name)} must be between ${date(args[0])} and ${date(args[1])}`;
        /* </i18n> */
    },
    /**
     * Shown when the user-provided value is not a valid email address.
     * @see {@link https://docs.formkit.com/essentials/validation#email}
     */
    email: 'Please enter a valid email address.',
    /**
     * Does not end with the specified value
     * @see {@link https://docs.formkit.com/essentials/validation#ends-with}
     */
    ends_with({ name, args }) {
        /* <i18n case="Shown when the user-provided value does not end with the substring supplied to the rule."> */
        return `${sentence(name)} doesn’t end with ${list(args)}.`;
        /* </i18n> */
    },
    /**
     * Is not an allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#is}
     */
    is({ name }) {
        /* <i18n case="Shown when the user-provided value is not one of the values supplied to the rule."> */
        return `${sentence(name)} is not an allowed value.`;
        /* </i18n> */
    },
    /**
     * Does not match specified length
     * @see {@link https://docs.formkit.com/essentials/validation#length}
     */
    length({ name, args: [first = 0, second = Infinity] }) {
        const min = Number(first) <= Number(second) ? first : second;
        const max = Number(second) >= Number(first) ? second : first;
        if (min == 1 && max === Infinity) {
            /* <i18n case="Shown when the length of the user-provided value is not at least one character."> */
            return `${sentence(name)} must be at least one character.`;
            /* </i18n> */
        }
        if (min == 0 && max) {
            /* <i18n case="Shown when first argument supplied to the rule is 0, and the user-provided value is longer than the max (the 2nd argument) supplied to the rule."> */
            return `${sentence(name)} must be less than or equal to ${max} characters.`;
            /* </i18n> */
        }
        if (min && max === Infinity) {
            /* <i18n case="Shown when the length of the user-provided value is less than the minimum supplied to the rule and there is no maximum supplied to the rule."> */
            return `${sentence(name)} must be greater than or equal to ${min} characters.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the length of the user-provided value is between the two lengths supplied to the rule."> */
        return `${sentence(name)} must be between ${min} and ${max} characters.`;
        /* </i18n> */
    },
    /**
     * Value is not a match
     * @see {@link https://docs.formkit.com/essentials/validation#matches}
     */
    matches({ name }) {
        /* <i18n case="Shown when the user-provided value does not match any of the values or RegExp patterns supplied to the rule. "> */
        return `${sentence(name)} is not an allowed value.`;
        /* </i18n> */
    },
    /**
     * Exceeds maximum allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#max}
     */
    max({ name, node: { value }, args }) {
        if (Array.isArray(value)) {
            /* <i18n case="Shown when the length of the array of user-provided values is longer than the max supplied to the rule."> */
            return `Cannot have more than ${args[0]} ${name}.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided value is greater than the maximum number supplied to the rule."> */
        return `${sentence(name)} must be less than or equal to ${args[0]}.`;
        /* </i18n> */
    },
    /**
     * The (field-level) value does not match specified mime type
     * @see {@link https://docs.formkit.com/essentials/validation#mime}
     */
    mime({ name, args }) {
        if (!args[0]) {
            /* <i18n case="Shown when no file formats were supplied to the rule."> */
            return 'No file formats allowed.';
            /* </i18n> */
        }
        /* <i18n case="Shown when the mime type of user-provided file does not match any mime types supplied to the rule."> */
        return `${sentence(name)} must be of the type: ${args[0]}`;
        /* </i18n> */
    },
    /**
     * Does not fulfill minimum allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#min}
     */
    min({ name, node: { value }, args }) {
        if (Array.isArray(value)) {
            /* <i18n case="Shown when the length of the array of user-provided values is shorter than the min supplied to the rule."> */
            return `Cannot have less than ${args[0]} ${name}.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided value is less than the minimum number supplied to the rule."> */
        return `${sentence(name)} must be at least ${args[0]}.`;
        /* </i18n> */
    },
    /**
     * Is not an allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#not}
     */
    not({ name, node: { value } }) {
        /* <i18n case="Shown when the user-provided value matches one of the values supplied to (and thus disallowed by) the rule."> */
        return `“${value}” is not an allowed ${name}.`;
        /* </i18n> */
    },
    /**
     *  Is not a number
     * @see {@link https://docs.formkit.com/essentials/validation#number}
     */
    number({ name }) {
        /* <i18n case="Shown when the user-provided value is not a number."> */
        return `${sentence(name)} must be a number.`;
        /* </i18n> */
    },
    /**
     * Required field.
     * @see {@link https://docs.formkit.com/essentials/validation#required}
     */
    required({ name }) {
        /* <i18n case="Shown when a user does not provide a value to a required input."> */
        return `${sentence(name)} is required.`;
        /* </i18n> */
    },
    /**
     * Does not start with specified value
     * @see {@link https://docs.formkit.com/essentials/validation#starts-with}
     */
    starts_with({ name, args }) {
        /* <i18n case="Shown when the user-provided value does not start with the substring supplied to the rule."> */
        return `${sentence(name)} doesn’t start with ${list(args)}.`;
        /* </i18n> */
    },
    /**
     * Is not a url
     * @see {@link https://docs.formkit.com/essentials/validation#url}
     */
    url() {
        /* <i18n case="Shown when the user-provided value is not a valid url."> */
        return `Please include a valid url.`;
        /* </i18n> */
    },
};

var en = /*#__PURE__*/Object.freeze({
    __proto__: null,
    ui: ui$b,
    validation: validation$b
});

/**
 * Here we can import additional helper functions to assist in formatting our
 * language. Feel free to add additional helper methods to libs/formats if it
 * assists in creating good validation messages for your locale.
 */
/**
 * Standard language for interface features.
 * @public
 */
const ui$a = {
    /**
     * Shown when a button to remove items is visible.
     */
    remove: 'Quitar',
    /**
     * Shown when there are multiple items to remove at the same time.
     */
    removeAll: 'Quitar todos',
    /**
     * Shown when all fields are not filled out correctly.
     */
    incomplete: 'Discúlpe, los campos no fueron completados correctamente.',
    /**
     * Shown in a button inside a form to submit the form.
     */
    submit: 'Enviar',
    /**
     * Shown when no files are selected.
     */
    noFiles: 'Archivo no seleccionado',
};
/**
 * These are all the possible strings that pertain to validation messages.
 * @public
 */
const validation$a = {
    /**
     * The value is not an accepted value.
     * @see {@link https://docs.formkit.com/essentials/validation#accepted}
     */
    accepted({ name }) {
        /* <i18n case="Shown when the user-provided value is not a valid 'accepted' value."> */
        return `Acepte el ${name} por favor.`;
        /* </i18n> */
    },
    /**
     * The date is not after
     * @see {@link https://docs.formkit.com/essentials/validation#date-after}
     */
    date_after({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date is not after the date supplied to the rule."> */
            return `${sentence(name)} debe ser posterior a ${date(args[0])}.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided date is not after today's date, since no date was supplied to the rule."> */
        return `${sentence(name)} debe ser una fecha futura.`;
        /* </i18n> */
    },
    /**
     * The value is not a letter.
     * @see {@link https://docs.formkit.com/essentials/validation#alpha}
     */
    alpha({ name }) {
        /* <i18n case="Shown when the user-provided value contains non-alphabetical characters."> */
        return `${sentence(name)} debe contener solo caractéres alfabéticos.`;
        /* </i18n> */
    },
    /**
     * The value is not alphanumeric
     * @see {@link https://docs.formkit.com/essentials/validation#alphanumeric}
     */
    alphanumeric({ name }) {
        /* <i18n case="Shown when the user-provided value contains non-alphanumeric characters."> */
        return `${sentence(name)} debe ser alfanumérico.`;
        /* </i18n> */
    },
    /**
     * The date is not before
     * @see {@link https://docs.formkit.com/essentials/validation#date-before}
     */
    date_before({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date is not before the date supplied to the rule."> */
            return `${sentence(name)} debe ser anterior a ${date(args[0])}.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided date is not before today's date, since no date was supplied to the rule."> */
        return `${sentence(name)} debe ser una fecha pasada.`;
        /* </i18n> */
    },
    /**
     * The value is not between two numbers
     * @see {@link https://docs.formkit.com/essentials/validation#between}
     */
    between({ name, args }) {
        if (isNaN(args[0]) || isNaN(args[1])) {
            /* <i18n case="Shown when any of the arguments supplied to the rule were not a number."> */
            return `El campo no fue completado correctamente y no puede ser enviado.`;
            /* </i18n> */
        }
        const [a, b] = order(args[0], args[1]);
        /* <i18n case="Shown when the user-provided value is not between two numbers."> */
        return `${sentence(name)} debe estar entre ${a} y ${b}.`;
        /* </i18n> */
    },
    /**
     * The confirmation field does not match
     * @see {@link https://docs.formkit.com/essentials/validation#confirm}
     */
    confirm({ name }) {
        /* <i18n case="Shown when the user-provided value does not equal the value of the matched input."> */
        return `${sentence(name)} no coincide.`;
        /* </i18n> */
    },
    /**
     * The value is not a valid date
     * @see {@link https://docs.formkit.com/essentials/validation#date-format}
     */
    date_format({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date does not satisfy the date format supplied to the rule."> */
            return `${sentence(name)} no es una fecha válida, por favor utilice el formato ${args[0]}`;
            /* </i18n> */
        }
        /* <i18n case="Shown when no date argument was supplied to the rule."> */
        return 'El campo no fue completado correctamente y no puede ser enviado.';
        /* </i18n> */
    },
    /**
     * Is not within expected date range
     * @see {@link https://docs.formkit.com/essentials/validation#date-between}
     */
    date_between({ name, args }) {
        /* <i18n case="Shown when the user-provided date is not between the start and end dates supplied to the rule. "> */
        return `${sentence(name)} debe estar entre ${date(args[0])} y ${date(args[1])}`;
        /* </i18n> */
    },
    /**
     * Shown when the user-provided value is not a valid email address.
     * @see {@link https://docs.formkit.com/essentials/validation#email}
     */
    email: 'Ingrese una dirección de correo electrónico válida por favor.',
    /**
     * Does not end with the specified value
     * @see {@link https://docs.formkit.com/essentials/validation#ends-with}
     */
    ends_with({ name, args }) {
        /* <i18n case="Shown when the user-provided value does not end with the substring supplied to the rule."> */
        return `${sentence(name)} no termina con ${list(args)}.`;
        /* </i18n> */
    },
    /**
     * Is not an allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#is}
     */
    is({ name }) {
        /* <i18n case="Shown when the user-provided value is not one of the values supplied to the rule."> */
        return `${sentence(name)} no es un valor permitido.`;
        /* </i18n> */
    },
    /**
     * Does not match specified length
     * @see {@link https://docs.formkit.com/essentials/validation#length}
     */
    length({ name, args: [first = 0, second = Infinity] }) {
        const min = Number(first) <= Number(second) ? first : second;
        const max = Number(second) >= Number(first) ? second : first;
        if (min == 1 && max === Infinity) {
            /* <i18n case="Shown when the length of the user-provided value is not at least one character."> */
            return `${sentence(name)} debe tener al menos una letra.`;
            /* </i18n> */
        }
        if (min == 0 && max) {
            /* <i18n case="Shown when first argument supplied to the rule is 0, and the user-provided value is longer than the max (the 2nd argument) supplied to the rule."> */
            return `${sentence(name)} debe tener como máximo ${max} caractéres.`;
            /* </i18n> */
        }
        if (min && max === Infinity) {
            /* <i18n case="Shown when the length of the user-provided value is less than the minimum supplied to the rule and there is no maximum supplied to the rule."> */
            return `${sentence(name)} debe tener como mínimo ${min} caractéres.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the length of the user-provided value is between the two lengths supplied to the rule."> */
        return `${sentence(name)} debe tener entre ${min} y ${max} caractéres.`;
        /* </i18n> */
    },
    /**
     * Value is not a match
     * @see {@link https://docs.formkit.com/essentials/validation#matches}
     */
    matches({ name }) {
        /* <i18n case="Shown when the user-provided value does not match any of the values or RegExp patterns supplied to the rule. "> */
        return `${sentence(name)} no es un valor permitido.`;
        /* </i18n> */
    },
    /**
     * Exceeds maximum allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#max}
     */
    max({ name, node: { value }, args }) {
        if (Array.isArray(value)) {
            /* <i18n case="Shown when the length of the array of user-provided values is longer than the max supplied to the rule."> */
            return `Cannot have more than ${args[0]} ${name}.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided value is greater than the maximum number supplied to the rule."> */
        return `${sentence(name)} debe ser menor o igual a ${args[0]}.`;
        /* </i18n> */
    },
    /**
     * The (field-level) value does not match specified mime type
     * @see {@link https://docs.formkit.com/essentials/validation#mime}
     */
    mime({ name, args }) {
        if (!args[0]) {
            /* <i18n case="Shown when no file formats were supplied to the rule."> */
            return 'No existen formatos de archivos permitidos.';
            /* </i18n> */
        }
        /* <i18n case="Shown when the mime type of user-provided file does not match any mime types supplied to the rule."> */
        return `${sentence(name)} debe ser del tipo: ${args[0]}`;
        /* </i18n> */
    },
    /**
     * Does not fulfill minimum allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#min}
     */
    min({ name, node: { value }, args }) {
        if (Array.isArray(value)) {
            /* <i18n case="Shown when the length of the array of user-provided values is shorter than the min supplied to the rule."> */
            return `Cannot have less than ${args[0]} ${name}.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided value is less than the minimum number supplied to the rule."> */
        return `${sentence(name)} debe ser de al menos ${args[0]}.`;
        /* </i18n> */
    },
    /**
     * Is not an allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#not}
     */
    not({ name, node: { value } }) {
        /* <i18n case="Shown when the user-provided value matches one of the values supplied to (and thus disallowed by) the rule."> */
        return `“${value}” no es un valor permitido de ${name}.`;
        /* </i18n> */
    },
    /**
     *  Is not a number
     * @see {@link https://docs.formkit.com/essentials/validation#number}
     */
    number({ name }) {
        /* <i18n case="Shown when the user-provided value is not a number."> */
        return `${sentence(name)} debe ser un número.`;
        /* </i18n> */
    },
    /**
     * Required field.
     * @see {@link https://docs.formkit.com/essentials/validation#required}
     */
    required({ name }) {
        /* <i18n case="Shown when a user does not provide a value to a required input."> */
        return `${sentence(name)} es requerido.`;
        /* </i18n> */
    },
    /**
     * Does not start with specified value
     * @see {@link https://docs.formkit.com/essentials/validation#starts-with}
     */
    starts_with({ name, args }) {
        /* <i18n case="Shown when the user-provided value does not start with the substring supplied to the rule."> */
        return `${sentence(name)} debe comenzar con ${list(args)}.`;
        /* </i18n> */
    },
    /**
     * Is not a url
     * @see {@link https://docs.formkit.com/essentials/validation#url}
     */
    url() {
        /* <i18n case="Shown when the user-provided value is not a valid url."> */
        return `Proporcione una URL válida por favor.`;
        /* </i18n> */
    },
};

var es = /*#__PURE__*/Object.freeze({
    __proto__: null,
    ui: ui$a,
    validation: validation$a
});

/**
 * Here we can import additional helper functions to assist in formatting our
 * language. Feel free to add additional helper methods to libs/formats if it
 * assists in creating good validation messages for your locale.
 */
/**
 * Standard language for interface features.
 * @public
 */
const ui$9 = {
    /**
     * Shown when a button to remove items is visible.
     */
    remove: 'حذف',
    /**
     * Shown when there are multiple items to remove at the same time.
     */
    removeAll: 'همه را حذف کنید',
    /**
     * Shown when all fields are not filled out correctly.
     */
    incomplete: 'همه فیلدها به‌درستی پر نشده‌اند',
    /**
     * Shown in a button inside a form to submit the form.
     */
    submit: 'ثبت',
    /**
     * Shown when no files are selected.
     */
    noFiles: 'هیچ فایلی انتخاب نشده است',
};
/**
 * These are all the possible strings that pertain to validation messages.
 * @public
 */
const validation$9 = {
    /**
     * The value is not an accepted value.
     * @see {@link https://docs.formkit.com/essentials/validation#accepted}
     */
    accepted({ name }) {
        /* <i18n case="Shown when the user-provided value is not a valid 'accepted' value."> */
        return `لطفاً ${name} را بپذیرید.`;
        /* </i18n> */
    },
    /**
     * The date is not after
     * @see {@link https://docs.formkit.com/essentials/validation#date-after}
     */
    date_after({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date is not after the date supplied to the rule."> */
            return `${sentence(name)} باید بعد از تاریخ ${date(args[0])} باشد.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided date is not after today's date, since no date was supplied to the rule."> */
        return `${sentence(name)} باید مربوط به آینده باشد.`;
        /* </i18n> */
    },
    /**
     * The value is not a letter.
     * @see {@link https://docs.formkit.com/essentials/validation#alpha}
     */
    alpha({ name }) {
        /* <i18n case="Shown when the user-provided value contains non-alphabetical characters."> */
        return `${sentence(name)} فقط میتواند شامل حروف الفبا باشد.`;
        /* </i18n> */
    },
    /**
     * The value is not alphanumeric
     * @see {@link https://docs.formkit.com/essentials/validation#alphanumeric}
     */
    alphanumeric({ name }) {
        /* <i18n case="Shown when the user-provided value contains non-alphanumeric characters."> */
        return `${sentence(name)} فقط میتواند شامل حروف و اعداد باشد.`;
        /* </i18n> */
    },
    /**
     * The date is not before
     * @see {@link https://docs.formkit.com/essentials/validation#date-before}
     */
    date_before({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date is not before the date supplied to the rule."> */
            return `${sentence(name)} باید قبل از تاریخ ${date(args[0])} باشد.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided date is not before today's date, since no date was supplied to the rule."> */
        return `${sentence(name)} باید مربوط به گذشته باشد.`;
        /* </i18n> */
    },
    /**
     * The value is not between two numbers
     * @see {@link https://docs.formkit.com/essentials/validation#between}
     */
    between({ name, args }) {
        if (isNaN(args[0]) || isNaN(args[1])) {
            /* <i18n case="Shown when any of the arguments supplied to the rule were not a number."> */
            return `این فیلد به اشتباه پیکربندی شده است و قابل ارسال نیست`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided value is not between two numbers."> */
        return `${sentence(name)} باید بین ${args[0]} و ${args[1]} باشد.`;
        /* </i18n> */
    },
    /**
     * The confirmation field does not match
     * @see {@link https://docs.formkit.com/essentials/validation#confirm}
     */
    confirm({ name }) {
        /* <i18n case="Shown when the user-provided value does not equal the value of the matched input."> */
        return `${sentence(name)} مطابقت ندارد.`;
        /* </i18n> */
    },
    /**
     * The value is not a valid date
     * @see {@link https://docs.formkit.com/essentials/validation#date-format}
     */
    date_format({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date does not satisfy the date format supplied to the rule."> */
            return `${sentence(name)} تاریخ معتبری نیست، لطفاً از قالب ${args[0]} استفاده کنید
`;
            /* </i18n> */
        }
        /* <i18n case="Shown when no date argument was supplied to the rule."> */
        return 'این فیلد به اشتباه پیکربندی شده است و قابل ارسال نیست';
        /* </i18n> */
    },
    /**
     * Is not within expected date range
     * @see {@link https://docs.formkit.com/essentials/validation#date-between}
     */
    date_between({ name, args }) {
        /* <i18n case="Shown when the user-provided date is not between the start and end dates supplied to the rule. "> */
        return `${sentence(name)} باید بین ${date(args[0])} و ${date(args[1])} باشد.`;
        /* </i18n> */
    },
    /**
     * Shown when the user-provided value is not a valid email address.
     * @see {@link https://docs.formkit.com/essentials/validation#email}
     */
    email: 'لطفا آدرس ایمیل معتبر وارد کنید.',
    /**
     * Does not end with the specified value
     * @see {@link https://docs.formkit.com/essentials/validation#ends-with}
     */
    ends_with({ name, args }) {
        /* <i18n case="Shown when the user-provided value does not end with the substring supplied to the rule."> */
        return `${sentence(name)} باید به ${list(args)} ختم شود.`;
        /* </i18n> */
    },
    /**
     * Is not an allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#is}
     */
    is({ name }) {
        /* <i18n case="Shown when the user-provided value is not one of the values supplied to the rule."> */
        return `${sentence(name)} مجاز نیست.`;
        /* </i18n> */
    },
    /**
     * Does not match specified length
     * @see {@link https://docs.formkit.com/essentials/validation#length}
     */
    length({ name, args: [first = 0, second = Infinity] }) {
        const min = first <= second ? first : second;
        const max = second >= first ? second : first;
        if (min == 1 && max === Infinity) {
            /* <i18n case="Shown when the length of the user-provided value is not at least one character."> */
            return `${sentence(name)} باید حداقل یک کاراکتر باشد.`;
            /* </i18n> */
        }
        if (min == 0 && max) {
            /* <i18n case="Shown when first argument supplied to the rule is 0, and the user-provided value is longer than the max (the 2nd argument) supplied to the rule."> */
            return `${sentence(name)} باید کمتر یا برابر با ${max} کاراکتر باشد.`;
            /* </i18n> */
        }
        if (min && max === Infinity) {
            /* <i18n case="Shown when the length of the user-provided value is less than the minimum supplied to the rule and there is no maximum supplied to the rule."> */
            return `${sentence(name)} باید بزرگتر یا برابر با ${min} کاراکتر باشد.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the length of the user-provided value is between the two lengths supplied to the rule."> */
        return `${sentence(name)} باید بین ${min} و ${max} کاراکتر باشد.`;
        /* </i18n> */
    },
    /**
     * Value is not a match
     * @see {@link https://docs.formkit.com/essentials/validation#matches}
     */
    matches({ name }) {
        /* <i18n case="Shown when the user-provided value does not match any of the values or RegExp patterns supplied to the rule. "> */
        return `${sentence(name)} مجاز نیست.`;
        /* </i18n> */
    },
    /**
     * Exceeds maximum allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#max}
     */
    max({ name, node: { value }, args }) {
        if (Array.isArray(value)) {
            /* <i18n case="Shown when the length of the array of user-provided values is longer than the max supplied to the rule."> */
            return `${name} نمی تواند بیش از ${args[0]} باشد.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided value is greater than the maximum number supplied to the rule."> */
        return `${sentence(name)} باید کمتر یا برابر با ${args[0]} باشد.`;
        /* </i18n> */
    },
    /**
     * The (field-level) value does not match specified mime type
     * @see {@link https://docs.formkit.com/essentials/validation#mime}
     */
    mime({ name, args }) {
        if (!args[0]) {
            /* <i18n case="Shown when no file formats were supplied to the rule."> */
            return 'فرمت فایل مجاز نیست.';
            /* </i18n> */
        }
        /* <i18n case="Shown when the mime type of user-provided file does not match any mime types supplied to the rule."> */
        return `${sentence(name)} باید از این نوع باشد: ${args[0]}`;
        /* </i18n> */
    },
    /**
     * Does not fulfill minimum allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#min}
     */
    min({ name, node: { value }, args }) {
        if (Array.isArray(value)) {
            /* <i18n case="Shown when the length of the array of user-provided values is shorter than the min supplied to the rule."> */
            return `${name} نمی تواند کمتر از ${args[0]} باشد.
`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided value is less than the minimum number supplied to the rule."> */
        return `${sentence(name)} باید حداقل ${args[0]} باشد.`;
        /* </i18n> */
    },
    /**
     * Is not an allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#not}
     */
    not({ name, node: { value } }) {
        /* <i18n case="Shown when the user-provided value matches one of the values supplied to (and thus disallowed by) the rule."> */
        return `"${value}" یک ${name} مجاز نیست.`;
        /* </i18n> */
    },
    /**
     *  Is not a number
     * @see {@link https://docs.formkit.com/essentials/validation#number}
     */
    number({ name }) {
        /* <i18n case="Shown when the user-provided value is not a number."> */
        return `${sentence(name)} باید عدد باشد.`;
        /* </i18n> */
    },
    /**
     * Required field.
     * @see {@link https://docs.formkit.com/essentials/validation#required}
     */
    required({ name }) {
        /* <i18n case="Shown when a user does not provide a value to a required input."> */
        return `پر کردن ${sentence(name)} اجباری است.`;
        /* </i18n> */
    },
    /**
     * Does not start with specified value
     * @see {@link https://docs.formkit.com/essentials/validation#starts-with}
     */
    starts_with({ name, args }) {
        /* <i18n case="Shown when the user-provided value does not start with the substring supplied to the rule."> */
        return `${sentence(name)} باید با ${list(args)} شروع شود.`;
        /* </i18n> */
    },
    /**
     * Is not a url
     * @see {@link https://docs.formkit.com/essentials/validation#url}
     */
    url() {
        /* <i18n case="Shown when the user-provided value is not a valid url."> */
        return `لطفاً آدرس اینترنتی معتبر وارد کنید.`;
        /* </i18n> */
    },
};

var fa = /*#__PURE__*/Object.freeze({
    __proto__: null,
    ui: ui$9,
    validation: validation$9
});

/**
 * Here we can import additional helper functions to assist in formatting our
 * language. Feel free to add additional helper methods to libs/formats if it
 * assists in creating good validation messages for your locale.
 */
/**
 * Standard language for interface features.
 * @public
 */
const ui$8 = {
    /**
     * Shown when a button to remove items is visible.
     */
    remove: 'Poista',
    /**
     * Shown when there are multiple items to remove at the same time.
     */
    removeAll: 'Poista kaikki',
    /**
     * Shown when all fields are not filled out correctly.
     */
    incomplete: 'Kaikkia kenttiä ei ole täytetty oikein.',
    /**
     * Shown in a button inside a form to submit the form.
     */
    submit: 'Tallenna',
    /**
     * Shown when no files are selected.
     */
    noFiles: 'Ei valittuja tiedostoja',
};
/**
 * These are all the possible strings that pertain to validation messages.
 * @public
 */
const validation$8 = {
    /**
     * The value is not an accepted value.
     * @see {@link https://docs.formkit.com/essentials/validation#accepted}
     */
    accepted({ name }) {
        /* <i18n case="Shown when the user-provided value is not a valid 'accepted' value."> */
        return `Ole hyvä ja hyväksy ${name}`;
        /* </i18n> */
    },
    /**
     * The date is not after
     * @see {@link https://docs.formkit.com/essentials/validation#date-after}
     */
    date_after({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date is not after the date supplied to the rule."> */
            return `${sentence(name)} tulee olla ${date(args[0])} jälkeen.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided date is not after today's date, since no date was supplied to the rule."> */
        return `${sentence(name)} on oltava tulevaisuudessa.`;
        /* </i18n> */
    },
    /**
     * The value is not a letter.
     * @see {@link https://docs.formkit.com/essentials/validation#alpha}
     */
    alpha({ name }) {
        /* <i18n case="Shown when the user-provided value contains non-alphabetical characters."> */
        return `${sentence(name)} saa sisältää vain kirjaimia.`;
        /* </i18n> */
    },
    /**
     * The value is not alphanumeric
     * @see {@link https://docs.formkit.com/essentials/validation#alphanumeric}
     */
    alphanumeric({ name }) {
        /* <i18n case="Shown when the user-provided value contains non-alphanumeric characters."> */
        return `${sentence(name)} saa sisältää vain kirjaimia ja numeroita.`;
        /* </i18n> */
    },
    /**
     * The date is not before
     * @see {@link https://docs.formkit.com/essentials/validation#date-before}
     */
    date_before({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date is not before the date supplied to the rule."> */
            return `${sentence(name)} tulee olla ennen: ${date(args[0])}.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided date is not before today's date, since no date was supplied to the rule."> */
        return `${sentence(name)} on oltava menneisyydessä.`;
        /* </i18n> */
    },
    /**
     * The value is not between two numbers
     * @see {@link https://docs.formkit.com/essentials/validation#between}
     */
    between({ name, args }) {
        if (isNaN(args[0]) || isNaN(args[1])) {
            /* <i18n case="Shown when any of the arguments supplied to the rule were not a number."> */
            return `Tämä kenttä on täytetty virheellisesti joten sitä ei voitu lähettää.`;
            /* </i18n> */
        }
        const [a, b] = order(args[0], args[1]);
        /* <i18n case="Shown when the user-provided value is not between two numbers."> */
        return `${sentence(name)} on oltava välillä ${a} - ${b} `;
        /* </i18n> */
    },
    /**
     * The confirmation field does not match
     * @see {@link https://docs.formkit.com/essentials/validation#confirm}
     */
    confirm({ name }) {
        /* <i18n case="Shown when the user-provided value does not equal the value of the matched input."> */
        return `${sentence(name)} ei täsmää.`;
        /* </i18n> */
    },
    /**
     * The value is not a valid date
     * @see {@link https://docs.formkit.com/essentials/validation#date-format}
     */
    date_format({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date does not satisfy the date format supplied to the rule."> */
            return `${sentence(name)} ei ole validi päivämäärä, ole hyvä ja syötä muodossa: ${args[0]}`;
            /* </i18n> */
        }
        /* <i18n case="Shown when no date argument was supplied to the rule."> */
        return 'Tämä kenttä on täytetty virheellisesti joten sitä ei voitu lähettää.';
        /* </i18n> */
    },
    /**
     * Is not within expected date range
     * @see {@link https://docs.formkit.com/essentials/validation#date-between}
     */
    date_between({ name, args }) {
        /* <i18n case="Shown when the user-provided date is not between the start and end dates supplied to the rule. "> */
        return `${sentence(name)} on oltava välillä ${date(args[0])} - ${date(args[1])}`;
        /* </i18n> */
    },
    /**
     * Shown when the user-provided value is not a valid email address.
     * @see {@link https://docs.formkit.com/essentials/validation#email}
     */
    email: 'Syötä validi sähköpostiosoite.',
    /**
     * Does not end with the specified value
     * @see {@link https://docs.formkit.com/essentials/validation#ends-with}
     */
    ends_with({ name, args }) {
        /* <i18n case="Shown when the user-provided value does not end with the substring supplied to the rule."> */
        return `${sentence(name)} tulee päättyä ${list(args)}.`;
        /* </i18n> */
    },
    /**
     * Is not an allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#is}
     */
    is({ name }) {
        /* <i18n case="Shown when the user-provided value is not one of the values supplied to the rule."> */
        return `${sentence(name)} ei ole sallittu vaihtoehto.`;
        /* </i18n> */
    },
    /**
     * Does not match specified length
     * @see {@link https://docs.formkit.com/essentials/validation#length}
     */
    length({ name, args: [first = 0, second = Infinity] }) {
        const min = Number(first) <= Number(second) ? first : second;
        const max = Number(second) >= Number(first) ? second : first;
        if (min == 1 && max === Infinity) {
            /* <i18n case="Shown when the length of the user-provided value is not at least one character."> */
            return `${sentence(name)} on oltava vähintään yksi merkki.`;
            /* </i18n> */
        }
        if (min == 0 && max) {
            /* <i18n case="Shown when first argument supplied to the rule is 0, and the user-provided value is longer than the max (the 2nd argument) supplied to the rule."> */
            return `${sentence(name)} on oltava ${max} tai alle merkkiä.`;
            /* </i18n> */
        }
        if (min && max === Infinity) {
            /* <i18n case="Shown when the length of the user-provided value is less than the minimum supplied to the rule and there is no maximum supplied to the rule."> */
            return `${sentence(name)} on oltava vähintään ${min} merkkiä.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the length of the user-provided value is between the two lengths supplied to the rule."> */
        return `${sentence(name)} on oltava vähintään ${min}, enintään ${max} merkkiä.`;
        /* </i18n> */
    },
    /**
     * Value is not a match
     * @see {@link https://docs.formkit.com/essentials/validation#matches}
     */
    matches({ name }) {
        /* <i18n case="Shown when the user-provided value does not match any of the values or RegExp patterns supplied to the rule. "> */
        return `${sentence(name)} ei ole sallittu arvo.`;
        /* </i18n> */
    },
    /**
     * Exceeds maximum allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#max}
     */
    max({ name, node: { value }, args }) {
        if (Array.isArray(value)) {
            /* <i18n case="Shown when the length of the array of user-provided values is longer than the max supplied to the rule."> */
            return `Valitse enintään ${args[0]} ${name} vaihtoehtoa.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided value is greater than the maximum number supplied to the rule."> */
        return `${sentence(name)} on oltava ${args[0]} tai alle.`;
        /* </i18n> */
    },
    /**
     * The (field-level) value does not match specified mime type
     * @see {@link https://docs.formkit.com/essentials/validation#mime}
     */
    mime({ name, args }) {
        if (!args[0]) {
            /* <i18n case="Shown when no file formats were supplied to the rule."> */
            return 'Tiedostoja ei sallita.';
            /* </i18n> */
        }
        /* <i18n case="Shown when the mime type of user-provided file does not match any mime types supplied to the rule."> */
        return `${sentence(name)} tulee olla ${args[0]}-tiedostotyyppiä.`;
        /* </i18n> */
    },
    /**
     * Does not fulfill minimum allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#min}
     */
    min({ name, node: { value }, args }) {
        if (Array.isArray(value)) {
            /* <i18n case="Shown when the length of the array of user-provided values is shorter than the min supplied to the rule."> */
            return `Valitse vähintään ${args[0]} ${name} vaihtoehtoa.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided value is less than the minimum number supplied to the rule."> */
        return `${sentence(name)} tulee olla ${args[0]} tai suurempi.`;
        /* </i18n> */
    },
    /**
     * Is not an allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#not}
     */
    not({ name, node: { value } }) {
        /* <i18n case="Shown when the user-provided value matches one of the values supplied to (and thus disallowed by) the rule."> */
        return `“${value}” ei ole sallittu ${name}.`;
        /* </i18n> */
    },
    /**
     *  Is not a number
     * @see {@link https://docs.formkit.com/essentials/validation#number}
     */
    number({ name }) {
        /* <i18n case="Shown when the user-provided value is not a number."> */
        return `Kentän ${sentence(name)} tulee olla numero.`;
        /* </i18n> */
    },
    /**
     * Required field.
     * @see {@link https://docs.formkit.com/essentials/validation#required}
     */
    required({ name }) {
        /* <i18n case="Shown when a user does not provide a value to a required input."> */
        return `${sentence(name)} vaaditaan.`;
        /* </i18n> */
    },
    /**
     * Does not start with specified value
     * @see {@link https://docs.formkit.com/essentials/validation#starts-with}
     */
    starts_with({ name, args }) {
        /* <i18n case="Shown when the user-provided value does not start with the substring supplied to the rule."> */
        return `${sentence(name)} on alettava ${list(args)}.`;
        /* </i18n> */
    },
    /**
     * Is not a url
     * @see {@link https://docs.formkit.com/essentials/validation#url}
     */
    url() {
        /* <i18n case="Shown when the user-provided value is not a valid url."> */
        return `Syötä validi url-osoite.`;
        /* </i18n> */
    },
};

var fi = /*#__PURE__*/Object.freeze({
    __proto__: null,
    ui: ui$8,
    validation: validation$8
});

/**
 * Here we can import additional helper functions to assist in formatting our
 * language. Feel free to add additional helper methods to libs/formats if it
 * assists in creating good validation messages for your locale.
 */
/**
 * Standard language for interface features.
 * @public
 */
const ui$7 = {
    /**
     * Shown when a button to remove items is visible.
     */
    remove: 'Supprimer',
    /**
     * Shown when there are multiple items to remove at the same time.
     */
    removeAll: 'Enlever tout',
    /**
     * Shown when all fields are not filled out correctly.
     */
    incomplete: 'Désolé, tous les champs ne sont pas remplit correctement.',
    /**
     * Shown in a button inside a form to submit the form.
     */
    submit: 'Valider',
    /**
     * Shown when no files are selected.
     */
    noFiles: 'Aucun fichier choisi',
};
/**
 * These are all the possible strings that pertain to validation messages.
 * @public
 */
const validation$7 = {
    /**
     * The value is not an accepted value.
     * @see {@link https://docs.formkit.com/essentials/validation#accepted}
     */
    accepted({ name }) {
        /* <i18n case="Shown when the user-provided value is not a valid 'accepted' value."> */
        return `Veuillez accepter le ${name}.`;
        /* </i18n> */
    },
    /**
     * The date is not after
     * @see {@link https://docs.formkit.com/essentials/validation#date-after}
     */
    date_after({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date is not after the date supplied to the rule."> */
            return `${sentence(name)} doit être après ${date(args[0])}.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided date is not after today's date, since no date was supplied to the rule."> */
        return `${sentence(name)} doit être dans le futur.`;
        /* </i18n> */
    },
    /**
     * The value is not a letter.
     * @see {@link https://docs.formkit.com/essentials/validation#alpha}
     */
    alpha({ name }) {
        /* <i18n case="Shown when the user-provided value contains non-alphabetical characters."> */
        return `${sentence(name)} ne peut contenir que des caractères alphabétiques.`;
        /* </i18n> */
    },
    /**
     * The value is not alphanumeric
     * @see {@link https://docs.formkit.com/essentials/validation#alphanumeric}
     */
    alphanumeric({ name }) {
        /* <i18n case="Shown when the user-provided value contains non-alphanumeric characters."> */
        return `${sentence(name)} ne peut contenir que des lettres et des chiffres.`;
        /* </i18n> */
    },
    /**
     * The date is not before
     * @see {@link https://docs.formkit.com/essentials/validation#date-before}
     */
    date_before({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date is not before the date supplied to the rule."> */
            return `${sentence(name)} doit être avant ${date(args[0])}.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided date is not before today's date, since no date was supplied to the rule."> */
        return `${sentence(name)} doit être dans le passé.`;
        /* </i18n> */
    },
    /**
     * The value is not between two numbers
     * @see {@link https://docs.formkit.com/essentials/validation#between}
     */
    between({ name, args }) {
        if (isNaN(args[0]) || isNaN(args[1])) {
            /* <i18n case="Shown when any of the arguments supplied to the rule were not a number."> */
            return `Ce champ a été configuré de manière incorrecte et ne peut pas être soumis.`;
            /* </i18n> */
        }
        const [a, b] = order(args[0], args[1]);
        /* <i18n case="Shown when the user-provided value is not between two numbers."> */
        return `${sentence(name)} doit être comprise entre ${a} et ${b}.`;
        /* </i18n> */
    },
    /**
     * The confirmation field does not match
     * @see {@link https://docs.formkit.com/essentials/validation#confirm}
     */
    confirm({ name }) {
        /* <i18n case="Shown when the user-provided value does not equal the value of the matched input."> */
        return `${sentence(name)} ne correspond pas.`;
        /* </i18n> */
    },
    /**
     * The value is not a valid date
     * @see {@link https://docs.formkit.com/essentials/validation#date-format}
     */
    date_format({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date does not satisfy the date format supplied to the rule."> */
            return `${sentence(name)} n'est pas une date valide, veuillez utiliser le format ${args[0]}`;
            /* </i18n> */
        }
        /* <i18n case="Shown when no date argument was supplied to the rule."> */
        return 'Ce champ a été configuré de manière incorrecte et ne peut pas être soumis.';
        /* </i18n> */
    },
    /**
     * Is not within expected date range
     * @see {@link https://docs.formkit.com/essentials/validation#date-between}
     */
    date_between({ name, args }) {
        /* <i18n case="Shown when the user-provided date is not between the start and end dates supplied to the rule. "> */
        return `${sentence(name)} doit être comprise entre ${date(args[0])} et ${date(args[1])}`;
        /* </i18n> */
    },
    /**
     * Shown when the user-provided value is not a valid email address.
     * @see {@link https://docs.formkit.com/essentials/validation#email}
     */
    email: 'Veuillez saisir une adresse email valide.',
    /**
     * Does not end with the specified value
     * @see {@link https://docs.formkit.com/essentials/validation#ends-with}
     */
    ends_with({ name, args }) {
        /* <i18n case="Shown when the user-provided value does not end with the substring supplied to the rule."> */
        return `${sentence(name)} ne se termine pas par ${list(args)}.`;
        /* </i18n> */
    },
    /**
     * Is not an allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#is}
     */
    is({ name }) {
        /* <i18n case="Shown when the user-provided value is not one of the values supplied to the rule."> */
        return `${sentence(name)} n'est pas une valeur autorisée.`;
        /* </i18n> */
    },
    /**
     * Does not match specified length
     * @see {@link https://docs.formkit.com/essentials/validation#length}
     */
    length({ name, args: [first = 0, second = Infinity] }) {
        const min = Number(first) <= Number(second) ? first : second;
        const max = Number(second) >= Number(first) ? second : first;
        if (min == 1 && max === Infinity) {
            /* <i18n case="Shown when the length of the user-provided value is not at least one character."> */
            return `${sentence(name)} doit comporter au moins un caractère.`;
            /* </i18n> */
        }
        if (min == 0 && max) {
            /* <i18n case="Shown when first argument supplied to the rule is 0, and the user-provided value is longer than the max (the 2nd argument) supplied to the rule."> */
            return `${sentence(name)} doit être inférieur ou égal à ${max} caractères.`;
            /* </i18n> */
        }
        if (min && max === Infinity) {
            /* <i18n case="Shown when the length of the user-provided value is less than the minimum supplied to the rule and there is no maximum supplied to the rule."> */
            return `${sentence(name)} doit être supérieur ou égal à ${min} caractères.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the length of the user-provided value is between the two lengths supplied to the rule."> */
        return `${sentence(name)} doit être comprise entre ${min} et ${max} caractères.`;
        /* </i18n> */
    },
    /**
     * Value is not a match
     * @see {@link https://docs.formkit.com/essentials/validation#matches}
     */
    matches({ name }) {
        /* <i18n case="Shown when the user-provided value does not match any of the values or RegExp patterns supplied to the rule. "> */
        return `${sentence(name)} n'est pas une valeur autorisée.`;
        /* </i18n> */
    },
    /**
     * Exceeds maximum allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#max}
     */
    max({ name, node: { value }, args }) {
        if (Array.isArray(value)) {
            /* <i18n case="Shown when the length of the array of user-provided values is longer than the max supplied to the rule."> */
            return `Ne peut pas avoir plus de ${args[0]} ${name}.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided value is greater than the maximum number supplied to the rule."> */
        return `${sentence(name)} doit être inférieur ou égal à ${args[0]}.`;
        /* </i18n> */
    },
    /**
     * The (field-level) value does not match specified mime type
     * @see {@link https://docs.formkit.com/essentials/validation#mime}
     */
    mime({ name, args }) {
        if (!args[0]) {
            /* <i18n case="Shown when no file formats were supplied to the rule."> */
            return 'Aucun format de fichier n’est autorisé';
            /* </i18n> */
        }
        /* <i18n case="Shown when the mime type of user-provided file does not match any mime types supplied to the rule."> */
        return `${sentence(name)} doit être du type: ${args[0]}`;
        /* </i18n> */
    },
    /**
     * Does not fulfill minimum allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#min}
     */
    min({ name, node: { value }, args }) {
        if (Array.isArray(value)) {
            /* <i18n case="Shown when the length of the array of user-provided values is shorter than the min supplied to the rule."> */
            return `Ne peut pas avoir moins de ${args[0]} ${name}.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided value is less than the minimum number supplied to the rule."> */
        return `${sentence(name)} doit être au moins de ${args[0]}.`;
        /* </i18n> */
    },
    /**
     * Is not an allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#not}
     */
    not({ name, node: { value } }) {
        /* <i18n case="Shown when the user-provided value matches one of the values supplied to (and thus disallowed by) the rule."> */
        return `“${value}” n'est pas un ${name} autorisé.`;
        /* </i18n> */
    },
    /**
     *  Is not a number
     * @see {@link https://docs.formkit.com/essentials/validation#number}
     */
    number({ name }) {
        /* <i18n case="Shown when the user-provided value is not a number."> */
        return `${sentence(name)} doit être un nombre.`;
        /* </i18n> */
    },
    /**
     * Required field.
     * @see {@link https://docs.formkit.com/essentials/validation#required}
     */
    required({ name }) {
        /* <i18n case="Shown when a user does not provide a value to a required input."> */
        return `${sentence(name)} est requis.`;
        /* </i18n> */
    },
    /**
     * Does not start with specified value
     * @see {@link https://docs.formkit.com/essentials/validation#starts-with}
     */
    starts_with({ name, args }) {
        /* <i18n case="Shown when the user-provided value does not start with the substring supplied to the rule."> */
        return `${sentence(name)} ne commence pas par ${list(args)}.`;
        /* </i18n> */
    },
    /**
     * Is not a url
     * @see {@link https://docs.formkit.com/essentials/validation#url}
     */
    url() {
        /* <i18n case="Shown when the user-provided value is not a valid url."> */
        return `Veuillez saisir une url valide.`;
        /* </i18n> */
    },
};

var fr = /*#__PURE__*/Object.freeze({
    __proto__: null,
    ui: ui$7,
    validation: validation$7
});

/**
 * Here we can import additional helper functions to assist in formatting our
 * language. Feel free to add additional helper methods to libs/formats if it
 * assists in creating good validation messages for your locale.
 */
/**
 * Standard language for interface features.
 * @public
 */
const ui$6 = {
    /**
     * Shown when a button to remove items is visible.
     */
    remove: 'מחק',
    /**
     * Shown when all fields are not filled out correctly.
     */
    incomplete: 'שים לב, לא כל השדות מלאים כראוי.',
    /**
     * Shown in a button inside a form to submit the form.
     */
    submit: 'שלח',
};
/**
 * These are all the possible strings that pertain to validation messages.
 * @public
 */
const validation$6 = {
    /**
     * The value is not an accepted value.
     * @see {@link https://docs.formkit.com/essentials/validation#accepted}
     */
    accepted({ name }) {
        /* <i18n case="Shown when the user-provided value is not a valid 'accepted' value."> */
        return `אנא אשר את ${name}.`;
        /* </i18n> */
    },
    /**
     * The date is not after
     * @see {@link https://docs.formkit.com/essentials/validation#date-after}
     */
    date_after({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date is not after the date supplied to the rule."> */
            return `${sentence(name)} חייב להיות אחרי ${date(args[0])}.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided date is not after today's date, since no date was supplied to the rule."> */
        return `${sentence(name)} חייב להיות בעתיד.`;
        /* </i18n> */
    },
    /**
     * The value is not a letter.
     * @see {@link https://docs.formkit.com/essentials/validation#alpha}
     */
    alpha({ name }) {
        /* <i18n case="Shown when the user-provided value contains non-alphabetical characters."> */
        return `${sentence(name)} חייב להכיל אותיות אלפבת.`;
        /* </i18n> */
    },
    /**
     * The value is not alphanumeric
     * @see {@link https://docs.formkit.com/essentials/validation#alphanumeric}
     */
    alphanumeric({ name }) {
        /* <i18n case="Shown when the user-provided value contains non-alphanumeric characters."> */
        return `${sentence(name)} יכול להכיל רק מספרים ואותיות.`;
        /* </i18n> */
    },
    /**
     * The date is not before
     * @see {@link https://docs.formkit.com/essentials/validation#date-before}
     */
    date_before({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date is not before the date supplied to the rule."> */
            return `${sentence(name)} חייב להיות לפני ${date(args[0])}.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided date is not before today's date, since no date was supplied to the rule."> */
        return `${sentence(name)} חייב להיות בעבר`;
        /* </i18n> */
    },
    /**
     * The value is not between two numbers
     * @see {@link https://docs.formkit.com/essentials/validation#between}
     */
    between({ name, args }) {
        if (isNaN(args[0]) || isNaN(args[1])) {
            /* <i18n case="Shown when any of the arguments supplied to the rule were not a number."> */
            return `שדה זה לא הוגדר כראוי ולא יכול להישלח.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided value is not between two numbers."> */
        return `${sentence(name)} חייב להיות בין ${args[0]} ו-${args[1]}.`;
        /* </i18n> */
    },
    /**
     * The confirmation field does not match
     * @see {@link https://docs.formkit.com/essentials/validation#confirm}
     */
    confirm({ name }) {
        /* <i18n case="Shown when the user-provided value does not equal the value of the matched input."> */
        return `${sentence(name)} לא מתאים.`;
        /* </i18n> */
    },
    /**
     * The value is not a valid date
     * @see {@link https://docs.formkit.com/essentials/validation#date-format}
     */
    date_format({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date does not satisfy the date format supplied to the rule."> */
            return `${sentence(name)} הוא לא תאריך תקין, אנא השתמש בפורמט ${args[0]}`;
            /* </i18n> */
        }
        /* <i18n case="Shown when no date argument was supplied to the rule."> */
        return 'שדה זה לא הוגדר כראוי ולא יכול להישלח.';
        /* </i18n> */
    },
    /**
     * Is not within expected date range
     * @see {@link https://docs.formkit.com/essentials/validation#date-between}
     */
    date_between({ name, args }) {
        /* <i18n case="Shown when the user-provided date is not between the start and end dates supplied to the rule. "> */
        return `${sentence(name)} חייב להיות בין ${date(args[0])} ו- ${date(args[1])}`;
        /* </i18n> */
    },
    /**
     * Shown when the user-provided value is not a valid email address.
     * @see {@link https://docs.formkit.com/essentials/validation#email}
     */
    email: 'אנא הקלד אימייל תקין.',
    /**
     * Does not end with the specified value
     * @see {@link https://docs.formkit.com/essentials/validation#ends-with}
     */
    ends_with({ name, args }) {
        /* <i18n case="Shown when the user-provided value does not end with the substring supplied to the rule."> */
        return `${sentence(name)} לא מסתיים ב- ${list(args)}.`;
        /* </i18n> */
    },
    /**
     * Is not an allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#is}
     */
    is({ name }) {
        /* <i18n case="Shown when the user-provided value is not one of the values supplied to the rule."> */
        return `${sentence(name)} הוא לא ערך מורשה.`;
        /* </i18n> */
    },
    /**
     * Does not match specified length
     * @see {@link https://docs.formkit.com/essentials/validation#length}
     */
    length({ name, args: [first = 0, second = Infinity] }) {
        const min = first <= second ? first : second;
        const max = second >= first ? second : first;
        if (min == 1 && max === Infinity) {
            /* <i18n case="Shown when the length of the user-provided value is not at least one character."> */
            return `${sentence(name)} חייב להיות לפחות תו אחד.`;
            /* </i18n> */
        }
        if (min == 0 && max) {
            /* <i18n case="Shown when first argument supplied to the rule is 0, and the user-provided value is longer than the max (the 2nd argument) supplied to the rule."> */
            return `${sentence(name)} חייב להיות פחות או שווה ל- ${max} תווים.`;
            /* </i18n> */
        }
        if (min && max === Infinity) {
            /* <i18n case="Shown when the length of the user-provided value is less than the minimum supplied to the rule and there is no maximum supplied to the rule."> */
            return `${sentence(name)} חייב ליות גדול או שווה ל- ${min} תווים.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the length of the user-provided value is between the two lengths supplied to the rule."> */
        return `${sentence(name)} חייב להיות בין ${min} ו- ${max} תווים.`;
        /* </i18n> */
    },
    /**
     * Value is not a match
     * @see {@link https://docs.formkit.com/essentials/validation#matches}
     */
    matches({ name }) {
        /* <i18n case="Shown when the user-provided value does not match any of the values or RegExp patterns supplied to the rule. "> */
        return `${sentence(name)} הוא לא ערך תקין.`;
        /* </i18n> */
    },
    /**
     * Exceeds maximum allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#max}
     */
    max({ name, node: { value }, args }) {
        if (Array.isArray(value)) {
            /* <i18n case="Shown when the length of the array of user-provided values is longer than the max supplied to the rule."> */
            return `${name} לא יכול להיות עם יותר מ- ${args[0]}.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided value is greater than the maximum number supplied to the rule."> */
        return `${sentence(name)} חייב להיות פחות או שווה ל- ${args[0]}.`;
        /* </i18n> */
    },
    /**
     * The (field-level) value does not match specified mime type
     * @see {@link https://docs.formkit.com/essentials/validation#mime}
     */
    mime({ name, args }) {
        if (!args[0]) {
            /* <i18n case="Shown when no file formats were supplied to the rule."> */
            return 'פורמט הקובץ לא מורשה.';
            /* </i18n> */
        }
        /* <i18n case="Shown when the mime type of user-provided file does not match any mime types supplied to the rule."> */
        return `${sentence(name)} חייב להיות מסוג: ${args[0]}`;
        /* </i18n> */
    },
    /**
     * Does not fulfill minimum allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#min}
     */
    min({ name, node: { value }, args }) {
        if (Array.isArray(value)) {
            /* <i18n case="Shown when the length of the array of user-provided values is shorter than the min supplied to the rule."> */
            return `${name} לא יכול להיות עם פחות מ- ${args[0]}.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided value is less than the minimum number supplied to the rule."> */
        return `${sentence(name)} חייב להיות לפחות ${args[0]}.`;
        /* </i18n> */
    },
    /**
     * Is not an allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#not}
     */
    not({ name, node: { value } }) {
        /* <i18n case="Shown when the user-provided value matches one of the values supplied to (and thus disallowed by) the rule."> */
        return `“${value}” לא מתאים ל- ${name}.`;
        /* </i18n> */
    },
    /**
     *  Is not a number
     * @see {@link https://docs.formkit.com/essentials/validation#number}
     */
    number({ name }) {
        /* <i18n case="Shown when the user-provided value is not a number."> */
        return `${sentence(name)} חייב להיות מספר.`;
        /* </i18n> */
    },
    /**
     * Required field.
     * @see {@link https://docs.formkit.com/essentials/validation#required}
     */
    required({ name }) {
        /* <i18n case="Shown when a user does not provide a value to a required input."> */
        return `${sentence(name)} הינו חובה.`;
        /* </i18n> */
    },
    /**
     * Does not start with specified value
     * @see {@link https://docs.formkit.com/essentials/validation#starts-with}
     */
    starts_with({ name, args }) {
        /* <i18n case="Shown when the user-provided value does not start with the substring supplied to the rule."> */
        return `${sentence(name)} לא מתחיל ב- ${list(args)}.`;
        /* </i18n> */
    },
    /**
     * Is not a url
     * @see {@link https://docs.formkit.com/essentials/validation#url}
     */
    url() {
        /* <i18n case="Shown when the user-provided value is not a valid url."> */
        return `אנא הקלד קישור תקין.`;
        /* </i18n> */
    },
};

var he = /*#__PURE__*/Object.freeze({
    __proto__: null,
    ui: ui$6,
    validation: validation$6
});

/**
 * Here we can import additional helper functions to assist in formatting our
 * language. Feel free to add additional helper methods to libs/formats if it
 * assists in creating good validation messages for your locale.
 */
/**
 * Standard language for interface features.
 * @public
 */
const ui$5 = {
    /**
     * Shown when a button to remove items is visible.
     */
    remove: 'Ukloni',
    /**
     * Shown when all fields are not filled out correctly.
     */
    incomplete: 'Pojedina polja nisu ispravno ispunjena.',
    /**
     * Shown in a button inside a form to submit the form.
     */
    submit: 'Predaj',
};
/**
 * These are all the possible strings that pertain to validation messages.
 * @public
 */
const validation$5 = {
    /**
     * The value is not an accepted value.
     * @see {@link https://docs.formkit.com/essentials/validation#accepted}
     */
    accepted({ name }) {
        /* <i18n case="Shown when the user-provided value is not a valid 'accepted' value."> */
        return `Potrebno je potvrditi ${name}.`;
        /* </i18n> */
    },
    /**
     * The date is not after
     * @see {@link https://docs.formkit.com/essentials/validation#date-after}
     */
    date_after({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date is not after the date supplied to the rule."> */
            return `${sentence(name)} mora biti u periodu poslije ${date(args[0])}.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided date is not after today's date, since no date was supplied to the rule."> */
        return `${sentence(name)} mora biti u budućnosti.`;
        /* </i18n> */
    },
    /**
     * The value is not a letter.
     * @see {@link https://docs.formkit.com/essentials/validation#alpha}
     */
    alpha({ name }) {
        /* <i18n case="Shown when the user-provided value contains non-alphabetical characters."> */
        return `${sentence(name)} mora sadržavati samo slova.`;
        /* </i18n> */
    },
    /**
     * The value is not alphanumeric
     * @see {@link https://docs.formkit.com/essentials/validation#alphanumeric}
     */
    alphanumeric({ name }) {
        /* <i18n case="Shown when the user-provided value contains non-alphanumeric characters."> */
        return `${sentence(name)} mora sadržavati slova i brojeve.`;
        /* </i18n> */
    },
    /**
     * The date is not before
     * @see {@link https://docs.formkit.com/essentials/validation#date-before}
     */
    date_before({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date is not before the date supplied to the rule."> */
            return `${sentence(name)} mora biti prije ${date(args[0])}.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided date is not before today's date, since no date was supplied to the rule."> */
        return `${sentence(name)} mora biti u prošlosti.`;
        /* </i18n> */
    },
    /**
     * The value is not between two numbers
     * @see {@link https://docs.formkit.com/essentials/validation#between}
     */
    between({ name, args }) {
        if (isNaN(args[0]) || isNaN(args[1])) {
            /* <i18n case="Shown when any of the arguments supplied to the rule were not a number."> */
            return `Format sadržaja nije ispravan i ne može biti predan.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided value is not between two numbers."> */
        return `${sentence(name)} mora biti između ${args[0]} i ${args[1]}.`;
        /* </i18n> */
    },
    /**
     * The confirmation field does not match
     * @see {@link https://docs.formkit.com/essentials/validation#confirm}
     */
    confirm({ name }) {
        /* <i18n case="Shown when the user-provided value does not equal the value of the matched input."> */
        return `${sentence(name)} ne odgovara zadanoj vrijednosti.`;
        /* </i18n> */
    },
    /**
     * The value is not a valid date
     * @see {@link https://docs.formkit.com/essentials/validation#date-format}
     */
    date_format({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date does not satisfy the date format supplied to the rule."> */
            return `${sentence(name)} nije ispravan format datuma. Molimo koristite sljedeći format: ${args[0]}`;
            /* </i18n> */
        }
        /* <i18n case="Shown when no date argument was supplied to the rule."> */
        return 'Ovo polje nije ispravno postavljeno i ne može biti predano.';
        /* </i18n> */
    },
    /**
     * Is not within expected date range
     * @see {@link https://docs.formkit.com/essentials/validation#date-between}
     */
    date_between({ name, args }) {
        /* <i18n case="Shown when the user-provided date is not between the start and end dates supplied to the rule. "> */
        return `${sentence(name)} mora biti vrijednost između ${date(args[0])} i ${date(args[1])}`;
        /* </i18n> */
    },
    /**
     * Shown when the user-provided value is not a valid email address.
     * @see {@link https://docs.formkit.com/essentials/validation#email}
     */
    email: 'Molimo upišite ispravnu email adresu.',
    /**
     * Does not end with the specified value
     * @see {@link https://docs.formkit.com/essentials/validation#ends-with}
     */
    ends_with({ name, args }) {
        /* <i18n case="Shown when the user-provided value does not end with the substring supplied to the rule."> */
        return `${sentence(name)} ne završava s ${list(args)}.`;
        /* </i18n> */
    },
    /**
     * Is not an allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#is}
     */
    is({ name }) {
        /* <i18n case="Shown when the user-provided value is not one of the values supplied to the rule."> */
        return `${sentence(name)} nije dopuštena vrijednost.`;
        /* </i18n> */
    },
    /**
     * Does not match specified length
     * @see {@link https://docs.formkit.com/essentials/validation#length}
     */
    length({ name, args: [first = 0, second = Infinity] }) {
        const min = first <= second ? first : second;
        const max = second >= first ? second : first;
        if (min == 1 && max === Infinity) {
            /* <i18n case="Shown when the length of the user-provided value is not at least one character."> */
            return `${sentence(name)} mora sadržavati barem jedan znak.`;
            /* </i18n> */
        }
        if (min == 0 && max) {
            /* <i18n case="Shown when first argument supplied to the rule is 0, and the user-provided value is longer than the max (the 2nd argument) supplied to the rule."> */
            return `${sentence(name)} mora imati ${max} ili manje znakova.`;
            /* </i18n> */
        }
        if (min && max === Infinity) {
            /* <i18n case="Shown when the length of the user-provided value is less than the minimum supplied to the rule and there is no maximum supplied to the rule."> */
            return `${sentence(name)} mora imati barem ${min} znakova.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the length of the user-provided value is between the two lengths supplied to the rule."> */
        return `Broj znakova za polje ${sentence(name)} mora biti između ${min} i ${max}.`;
        /* </i18n> */
    },
    /**
     * Value is not a match
     * @see {@link https://docs.formkit.com/essentials/validation#matches}
     */
    matches({ name }) {
        /* <i18n case="Shown when the user-provided value does not match any of the values or RegExp patterns supplied to the rule. "> */
        return `${sentence(name)} nije dozvoljena vrijednost.`;
        /* </i18n> */
    },
    /**
     * Exceeds maximum allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#max}
     */
    max({ name, node: { value }, args }) {
        if (Array.isArray(value)) {
            /* <i18n case="Shown when the length of the array of user-provided values is longer than the max supplied to the rule."> */
            return `Ne smije imati više od ${args[0]} ${name} polja.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided value is greater than the maximum number supplied to the rule."> */
        return `${sentence(name)} mora imati vrijednost manju ili jednaku ${args[0]}.`;
        /* </i18n> */
    },
    /**
     * The (field-level) value does not match specified mime type
     * @see {@link https://docs.formkit.com/essentials/validation#mime}
     */
    mime({ name, args }) {
        if (!args[0]) {
            /* <i18n case="Shown when no file formats were supplied to the rule."> */
            return 'Format datoteke nije dozvoljen.';
            /* </i18n> */
        }
        /* <i18n case="Shown when the mime type of user-provided file does not match any mime types supplied to the rule."> */
        return `Format datoteke na polju ${sentence(name)} mora odgovarati: ${args[0]}`;
        /* </i18n> */
    },
    /**
     * Does not fulfill minimum allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#min}
     */
    min({ name, node: { value }, args }) {
        if (Array.isArray(value)) {
            /* <i18n case="Shown when the length of the array of user-provided values is shorter than the min supplied to the rule."> */
            return `Broj upisanih vrijednosti na polju ${name} mora biti barem ${args[0]}.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided value is less than the minimum number supplied to the rule."> */
        return `${sentence(name)} mora biti barem ${args[0]}.`;
        /* </i18n> */
    },
    /**
     * Is not an allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#not}
     */
    not({ name, node: { value } }) {
        /* <i18n case="Shown when the user-provided value matches one of the values supplied to (and thus disallowed by) the rule."> */
        return `“${value}” nije dozvoljena vrijednost na polju ${name}.`;
        /* </i18n> */
    },
    /**
     *  Is not a number
     * @see {@link https://docs.formkit.com/essentials/validation#number}
     */
    number({ name }) {
        /* <i18n case="Shown when the user-provided value is not a number."> */
        return `${sentence(name)} mora biti broj.`;
        /* </i18n> */
    },
    /**
     * Required field.
     * @see {@link https://docs.formkit.com/essentials/validation#required}
     */
    required({ name }) {
        /* <i18n case="Shown when a user does not provide a value to a required input."> */
        return `${sentence(name)} je obavezno.`;
        /* </i18n> */
    },
    /**
     * Does not start with specified value
     * @see {@link https://docs.formkit.com/essentials/validation#starts-with}
     */
    starts_with({ name, args }) {
        /* <i18n case="Shown when the user-provided value does not start with the substring supplied to the rule."> */
        return `${sentence(name)} ne počinje s ${list(args)}.`;
        /* </i18n> */
    },
    /**
     * Is not a url
     * @see {@link https://docs.formkit.com/essentials/validation#url}
     */
    url() {
        /* <i18n case="Shown when the user-provided value is not a valid url."> */
        return `Molimo unesite ispravnu poveznicu.`;
        /* </i18n> */
    },
};

var hr = /*#__PURE__*/Object.freeze({
    __proto__: null,
    ui: ui$5,
    validation: validation$5
});

/**
 * Here we can import additional helper functions to assist in formatting our
 * language. Feel free to add additional helper methods to libs/formats if it
 * assists in creating good validation messages for your locale.
 */
/**
 * Standard language for interface features.
 * @public
 */
const ui$4 = {
    /**
     * Shown when a button to remove items is visible.
     */
    remove: 'Verwijder',
    /**
     * Shown when all fields are not filled out correctly.
     */
    incomplete: 'Sorry, niet alle velden zijn correct ingevuld.',
    /**
     * Shown in a button inside a form to submit the form.
     */
    submit: 'Versturen',
};
/**
 * These are all the possible strings that pertain to validation messages.
 * @public
 */
const validation$4 = {
    /**
     * The value is not an accepted value.
     * @see {@link https://docs.formkit.com/essentials/validation#accepted}
     */
    accepted({ name }) {
        /* <i18n case="Shown when the user-provided value is not a valid 'accepted' value."> */
        return `Accepteer de ${name}.`;
        /* </i18n> */
    },
    /**
     * The date is not after
     * @see {@link https://docs.formkit.com/essentials/validation#date-after}
     */
    date_after({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date is not after the date supplied to the rule."> */
            return `${sentence(name)} moet na ${date(args[0])} zijn.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided date is not after today's date, since no date was supplied to the rule."> */
        return `${sentence(name)} moet in de toekomst liggen.`;
        /* </i18n> */
    },
    /**
     * The value is not a letter.
     * @see {@link https://docs.formkit.com/essentials/validation#alpha}
     */
    alpha({ name }) {
        /* <i18n case="Shown when the user-provided value contains non-alphabetical characters."> */
        return `${sentence(name)} mag alleen alfabetische tekens bevatten.`;
        /* </i18n> */
    },
    /**
     * The value is not alphanumeric
     * @see {@link https://docs.formkit.com/essentials/validation#alphanumeric}
     */
    alphanumeric({ name }) {
        /* <i18n case="Shown when the user-provided value contains non-alphanumeric characters."> */
        return `${sentence(name)} mag alleen letters en cijfers bevatten.`;
        /* </i18n> */
    },
    /**
     * The date is not before
     * @see {@link https://docs.formkit.com/essentials/validation#date-before}
     */
    date_before({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date is not before the date supplied to the rule."> */
            return `${sentence(name)} moet vóór ${date(args[0])} vallen.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided date is not before today's date, since no date was supplied to the rule."> */
        return `${sentence(name)} moet in het verleden zijn.`;
        /* </i18n> */
    },
    /**
     * The value is not between two numbers
     * @see {@link https://docs.formkit.com/essentials/validation#between}
     */
    between({ name, args }) {
        if (isNaN(args[0]) || isNaN(args[1])) {
            /* <i18n case="Shown when any of the arguments supplied to the rule were not a number."> */
            return `Dit veld is verkeerd geconfigureerd en kan niet worden ingediend.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided value is not between two numbers."> */
        return `${sentence(name)} moet tussen ${args[0]} en ${args[1]} liggen.`;
        /* </i18n> */
    },
    /**
     * The confirmation field does not match
     * @see {@link https://docs.formkit.com/essentials/validation#confirm}
     */
    confirm({ name }) {
        /* <i18n case="Shown when the user-provided value does not equal the value of the matched input."> */
        return `${sentence(name)} komt niet overeen.`;
        /* </i18n> */
    },
    /**
     * The value is not a valid date
     * @see {@link https://docs.formkit.com/essentials/validation#date-format}
     */
    date_format({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date does not satisfy the date format supplied to the rule."> */
            return `${sentence(name)} is geen geldige datum, gebruik de notatie ${args[0]}`;
            /* </i18n> */
        }
        /* <i18n case="Shown when no date argument was supplied to the rule."> */
        return 'Dit veld is onjuist geconfigureerd en kan niet worden verzonden';
        /* </i18n> */
    },
    /**
     * Is not within expected date range
     * @see {@link https://docs.formkit.com/essentials/validation#date-between}
     */
    date_between({ name, args }) {
        /* <i18n case="Shown when the user-provided date is not between the start and end dates supplied to the rule. "> */
        return `${sentence(name)} moet tussen ${date(args[0])} en ${date(args[1])} liggen`;
        /* </i18n> */
    },
    /**
     * Shown when the user-provided value is not a valid email address.
     * @see {@link https://docs.formkit.com/essentials/validation#email}
     */
    email: 'Vul een geldig e-mailadres in.',
    /**
     * Does not end with the specified value
     * @see {@link https://docs.formkit.com/essentials/validation#ends-with}
     */
    ends_with({ name, args }) {
        /* <i18n case="Shown when the user-provided value does not end with the substring supplied to the rule."> */
        return `${sentence(name)} eindigt niet met ${list(args)}.`;
        /* </i18n> */
    },
    /**
     * Is not an allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#is}
     */
    is({ name }) {
        /* <i18n case="Shown when the user-provided value is not one of the values supplied to the rule."> */
        return `${sentence(name)} is geen toegestane waarde.`;
        /* </i18n> */
    },
    /**
     * Does not match specified length
     * @see {@link https://docs.formkit.com/essentials/validation#length}
     */
    length({ name, args: [first = 0, second = Infinity] }) {
        const min = first <= second ? first : second;
        const max = second >= first ? second : first;
        if (min == 1 && max === Infinity) {
            /* <i18n case="Shown when the length of the user-provided value is not at least one character."> */
            return `${sentence(name)} moet minimaal één teken zijn.`;
            /* </i18n> */
        }
        if (min == 0 && max) {
            /* <i18n case="Shown when first argument supplied to the rule is 0, and the user-provided value is longer than the max (the 2nd argument) supplied to the rule."> */
            return `${sentence(name)} moet kleiner zijn dan of gelijk zijn aan ${max} tekens.`;
            /* </i18n> */
        }
        if (min && max === Infinity) {
            /* <i18n case="Shown when the length of the user-provided value is less than the minimum supplied to the rule and there is no maximum supplied to the rule."> */
            return `${sentence(name)} moet groter zijn dan of gelijk zijn aan ${min} tekens.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the length of the user-provided value is between the two lengths supplied to the rule."> */
        return `${sentence(name)} moet tussen de ${min} en ${max} tekens bevatten.`;
        /* </i18n> */
    },
    /**
     * Value is not a match
     * @see {@link https://docs.formkit.com/essentials/validation#matches}
     */
    matches({ name }) {
        /* <i18n case="Shown when the user-provided value does not match any of the values or RegExp patterns supplied to the rule. "> */
        return `${sentence(name)} is geen toegestane waarde.`;
        /* </i18n> */
    },
    /**
     * Exceeds maximum allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#max}
     */
    max({ name, node: { value }, args }) {
        if (Array.isArray(value)) {
            /* <i18n case="Shown when the length of the array of user-provided values is longer than the max supplied to the rule."> */
            return `Mag niet meer dan ${args[0]} ${name} hebben.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided value is greater than the maximum number supplied to the rule."> */
        return `${sentence(name)} moet kleiner zijn dan of gelijk zijn aan ${args[0]}.`;
        /* </i18n> */
    },
    /**
     * The (field-level) value does not match specified mime type
     * @see {@link https://docs.formkit.com/essentials/validation#mime}
     */
    mime({ name, args }) {
        if (!args[0]) {
            /* <i18n case="Shown when no file formats were supplied to the rule."> */
            return 'Geen bestandsformaten toegestaan.';
            /* </i18n> */
        }
        /* <i18n case="Shown when the mime type of user-provided file does not match any mime types supplied to the rule."> */
        return `${sentence(name)} moet van het type: ${args[0]} zijn`;
        /* </i18n> */
    },
    /**
     * Does not fulfill minimum allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#min}
     */
    min({ name, node: { value }, args }) {
        if (Array.isArray(value)) {
            /* <i18n case="Shown when the length of the array of user-provided values is shorter than the min supplied to the rule."> */
            return `Mag niet minder dan ${args[0]} ${name} hebben.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided value is less than the minimum number supplied to the rule."> */
        return `${sentence(name)} moet minimaal ${args[0]} zijn.`;
        /* </i18n> */
    },
    /**
     * Is not an allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#not}
     */
    not({ name, node: { value } }) {
        /* <i18n case="Shown when the user-provided value matches one of the values supplied to (and thus disallowed by) the rule."> */
        return `"${value}" is geen toegestane ${name}.`;
        /* </i18n> */
    },
    /**
     *  Is not a number
     * @see {@link https://docs.formkit.com/essentials/validation#number}
     */
    number({ name }) {
        /* <i18n case="Shown when the user-provided value is not a number."> */
        return `${sentence(name)} moet een getal zijn.`;
        /* </i18n> */
    },
    /**
     * Required field.
     * @see {@link https://docs.formkit.com/essentials/validation#required}
     */
    required({ name }) {
        /* <i18n case="Shown when a user does not provide a value to a required input."> */
        return `${sentence(name)} is verplicht.`;
        /* </i18n> */
    },
    /**
     * Does not start with specified value
     * @see {@link https://docs.formkit.com/essentials/validation#starts-with}
     */
    starts_with({ name, args }) {
        /* <i18n case="Shown when the user-provided value does not start with the substring supplied to the rule."> */
        return `${sentence(name)} begint niet met ${list(args)}.`;
        /* </i18n> */
    },
    /**
     * Is not a url
     * @see {@link https://docs.formkit.com/essentials/validation#url}
     */
    url() {
        /* <i18n case="Shown when the user-provided value is not a valid url."> */
        return `Voeg een geldige url toe.`;
        /* </i18n> */
    },
};

var nl = /*#__PURE__*/Object.freeze({
    __proto__: null,
    ui: ui$4,
    validation: validation$4
});

/**
 * Here we can import additional helper functions to assist in formatting our
 * language. Feel free to add additional helper methods to libs/formats if it
 * assists in creating good validation messages for your locale.
 */
/**
 * Standard language for interface features.
 * @public
 */
const ui$3 = {
    /**
     * Shown when a button to remove items is visible.
     */
    remove: 'Remover',
    /**
     * Shown when there are multiple items to remove at the same time.
     */
    removeAll: 'Deletar tudo',
    /**
     * Shown when all fields are not filled out correctly.
     */
    incomplete: 'Desculpe, nem todos os campos foram preenchidos corretamente.',
    /**
     * Shown in a button inside a form to submit the form.
     */
    submit: 'Enviar',
    /**
     * Shown when no files are selected.
     */
    noFiles: 'Nenhum arquivo',
};
/**
 * These are all the possible strings that pertain to validation messages.
 * @public
 */
const validation$3 = {
    /**
     * The value is not an accepted value.
     * @see {@link https://docs.formkit.com/essentials/validation#accepted}
     */
    accepted({ name }) {
        /* <i18n case="Shown when the user-provided value is not a valid 'accepted' value."> */
        return `Por favor aceite o ${name}.`;
        /* </i18n> */
    },
    /**
     * The date is not after
     * @see {@link https://docs.formkit.com/essentials/validation#date-after}
     */
    date_after({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date is not after the date supplied to the rule."> */
            return `${sentence(name)} deve ser posterior a ${date(args[0])}.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided date is not after today's date, since no date was supplied to the rule."> */
        return `${sentence(name)} deve ser no futuro.`;
        /* </i18n> */
    },
    /**
     * The value is not a letter.
     * @see {@link https://docs.formkit.com/essentials/validation#alpha}
     */
    alpha({ name }) {
        /* <i18n case="Shown when the user-provided value contains non-alphabetical characters."> */
        return `${sentence(name)} só pode conter caracteres do alfabeto.`;
        /* </i18n> */
    },
    /**
     * The value is not alphanumeric
     * @see {@link https://docs.formkit.com/essentials/validation#alphanumeric}
     */
    alphanumeric({ name }) {
        /* <i18n case="Shown when the user-provided value contains non-alphanumeric characters."> */
        return `${sentence(name)} só pode ter letras e números.`;
        /* </i18n> */
    },
    /**
     * The date is not before
     * @see {@link https://docs.formkit.com/essentials/validation#date-before}
     */
    date_before({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date is not before the date supplied to the rule."> */
            return `${sentence(name)} deve ser anterior a ${date(args[0])}.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided date is not before today's date, since no date was supplied to the rule."> */
        return `${sentence(name)} deve ser anterior a data atual.`;
        /* </i18n> */
    },
    /**
     * The value is not between two numbers
     * @see {@link https://docs.formkit.com/essentials/validation#between}
     */
    between({ name, args }) {
        if (isNaN(args[0]) || isNaN(args[1])) {
            /* <i18n case="Shown when any of the arguments supplied to the rule were not a number."> */
            return `O campo foi configurado incorretamente e não pode ser enviado.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided value is not between two numbers."> */
        return `${sentence(name)} deve estar entre ${args[0]} e ${args[1]}.`;
        /* </i18n> */
    },
    /**
     * The confirmation field does not match
     * @see {@link https://docs.formkit.com/essentials/validation#confirm}
     */
    confirm({ name }) {
        /* <i18n case="Shown when the user-provided value does not equal the value of the matched input."> */
        return `${sentence(name)} não confere.`;
        /* </i18n> */
    },
    /**
     * The value is not a valid date
     * @see {@link https://docs.formkit.com/essentials/validation#date-format}
     */
    date_format({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date does not satisfy the date format supplied to the rule."> */
            return `${sentence(name)} não é uma data válida, por favor use o formato ${args[0]}`;
            /* </i18n> */
        }
        /* <i18n case="Shown when no date argument was supplied to the rule."> */
        return 'O campo foi configurado incorretamente e não pode ser enviado.';
        /* </i18n> */
    },
    /**
     * Is not within expected date range
     * @see {@link https://docs.formkit.com/essentials/validation#date-between}
     */
    date_between({ name, args }) {
        /* <i18n case="Shown when the user-provided date is not between the start and end dates supplied to the rule. "> */
        return `${sentence(name)} deve ser entre ${date(args[0])} e ${date(args[1])}`;
        /* </i18n> */
    },
    /**
     * Shown when the user-provided value is not a valid email address.
     * @see {@link https://docs.formkit.com/essentials/validation#email}
     */
    email: 'Por favor informe um e-mail válido.',
    /**
     * Does not end with the specified value
     * @see {@link https://docs.formkit.com/essentials/validation#ends-with}
     */
    ends_with({ name, args }) {
        /* <i18n case="Shown when the user-provided value does not end with the substring supplied to the rule."> */
        return `${sentence(name)} não termina com ${list(args)}.`;
        /* </i18n> */
    },
    /**
     * Is not an allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#is}
     */
    is({ name }) {
        /* <i18n case="Shown when the user-provided value is not one of the values supplied to the rule."> */
        return `${sentence(name)} não é um valor permitido.`;
        /* </i18n> */
    },
    /**
     * Does not match specified length
     * @see {@link https://docs.formkit.com/essentials/validation#length}
     */
    length({ name, args: [first = 0, second = Infinity] }) {
        const min = first <= second ? first : second;
        const max = second >= first ? second : first;
        if (min == 1 && max === Infinity) {
            /* <i18n case="Shown when the length of the user-provided value is not at least one character."> */
            return `${sentence(name)} deve ter ao menos um caractere.`;
            /* </i18n> */
        }
        if (min == 0 && max) {
            /* <i18n case="Shown when first argument supplied to the rule is 0, and the user-provided value is longer than the max (the 2nd argument) supplied to the rule."> */
            return `${sentence(name)} não pode ter mais que ${max} caracteres.`;
            /* </i18n> */
        }
        if (min && max === Infinity) {
            /* <i18n case="Shown when the length of the user-provided value is less than the minimum supplied to the rule and there is no maximum supplied to the rule."> */
            return `${sentence(name)} deve ter no mínimo ${min} caracteres.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the length of the user-provided value is between the two lengths supplied to the rule."> */
        return `${sentence(name)} deve ter entre ${min} e ${max} caracteres.`;
        /* </i18n> */
    },
    /**
     * Value is not a match
     * @see {@link https://docs.formkit.com/essentials/validation#matches}
     */
    matches({ name }) {
        /* <i18n case="Shown when the user-provided value does not match any of the values or RegExp patterns supplied to the rule. "> */
        return `${sentence(name)} não é um valor permitido.`;
        /* </i18n> */
    },
    /**
     * Exceeds maximum allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#max}
     */
    max({ name, node: { value }, args }) {
        if (Array.isArray(value)) {
            /* <i18n case="Shown when the length of the array of user-provided values is longer than the max supplied to the rule."> */
            return `Não pode ter mais que ${args[0]} ${name}.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided value is greater than the maximum number supplied to the rule."> */
        return `${sentence(name)} deve ser igual ou menor que ${args[0]}.`;
        /* </i18n> */
    },
    /**
     * The (field-level) value does not match specified mime type
     * @see {@link https://docs.formkit.com/essentials/validation#mime}
     */
    mime({ name, args }) {
        if (!args[0]) {
            /* <i18n case="Shown when no file formats were supplied to the rule."> */
            return 'Nenhum formato de arquivo permitido.';
            /* </i18n> */
        }
        /* <i18n case="Shown when the mime type of user-provided file does not match any mime types supplied to the rule."> */
        return `${sentence(name)} deve ser do tipo: ${args[0]}`;
        /* </i18n> */
    },
    /**
     * Does not fulfill minimum allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#min}
     */
    min({ name, node: { value }, args }) {
        if (Array.isArray(value)) {
            /* <i18n case="Shown when the length of the array of user-provided values is shorter than the min supplied to the rule."> */
            return `Não pode ter menos que ${args[0]} ${name}.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided value is less than the minimum number supplied to the rule."> */
        return `${sentence(name)} deve ter pelo menos ${args[0]}.`;
        /* </i18n> */
    },
    /**
     * Is not an allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#not}
     */
    not({ name, node: { value } }) {
        /* <i18n case="Shown when the user-provided value matches one of the values supplied to (and thus disallowed by) the rule."> */
        return `“${value}” não permite ${name}.`;
        /* </i18n> */
    },
    /**
     *  Is not a number
     * @see {@link https://docs.formkit.com/essentials/validation#number}
     */
    number({ name }) {
        /* <i18n case="Shown when the user-provided value is not a number."> */
        return `${sentence(name)} deve ser um número.`;
        /* </i18n> */
    },
    /**
     * Required field.
     * @see {@link https://docs.formkit.com/essentials/validation#required}
     */
    required({ name }) {
        /* <i18n case="Shown when a user does not provide a value to a required input."> */
        return `${sentence(name)} é obrigatório.`;
        /* </i18n> */
    },
    /**
     * Does not start with specified value
     * @see {@link https://docs.formkit.com/essentials/validation#starts-with}
     */
    starts_with({ name, args }) {
        /* <i18n case="Shown when the user-provided value does not start with the substring supplied to the rule."> */
        return `${sentence(name)} não começa com ${list(args)}.`;
        /* </i18n> */
    },
    /**
     * Is not a url
     * @see {@link https://docs.formkit.com/essentials/validation#url}
     */
    url() {
        /* <i18n case="Shown when the user-provided value is not a valid url."> */
        return `Por favor informe um url válido.`;
        /* </i18n> */
    },
};

var pt = /*#__PURE__*/Object.freeze({
    __proto__: null,
    ui: ui$3,
    validation: validation$3
});

/**
 * Here we can import additional helper functions to assist in formatting our
 * language. Feel free to add additional helper methods to libs/formats if it
 * assists in creating good validation messages for your locale.
 */
/**
 * Standard language for interface features.
 * @public
 */
const ui$2 = {
    /**
     * Shown when a button to remove items is visible.
     */
    remove: 'Удалить',
    /**
     * Shown when there are multiple items to remove at the same time.
     */
    removeAll: 'Убрать все',
    /**
     * Shown when all fields are not filled out correctly.
     */
    incomplete: 'Извините, не все поля заполнены верно.',
    /**
     * Shown in a button inside a form to submit the form.
     */
    submit: 'Отправить',
    /**
     * Shown when no files are selected.
     */
    noFiles: 'Файл не выбран',
};
/**
 * These are all the possible strings that pertain to validation messages.
 * @public
 */
const validation$2 = {
    /**
     * The value is not an accepted value.
     * @see {@link https://docs.formkit.com/essentials/validation#accepted}
     */
    accepted({ name }) {
        /* <i18n case="Shown when the user-provided value is not a valid 'accepted' value."> */
        return `Пожалуйста, примите ${name}.`;
        /* </i18n> */
    },
    /**
     * The date is not after
     * @see {@link https://docs.formkit.com/essentials/validation#date-after}
     */
    date_after({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date is not after the date supplied to the rule."> */
            return `Дата ${sentence(name)} должна быть позже ${date(args[0])}.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided date is not after today's date, since no date was supplied to the rule."> */
        return `Дата ${sentence(name)} должна быть в будущем.`;
        /* </i18n> */
    },
    /**
     * The value is not a letter.
     * @see {@link https://docs.formkit.com/essentials/validation#alpha}
     */
    alpha({ name }) {
        /* <i18n case="Shown when the user-provided value contains non-alphabetical characters."> */
        return `Поле ${sentence(name)} может содержать только буквы.`;
        /* </i18n> */
    },
    /**
     * The value is not alphanumeric
     * @see {@link https://docs.formkit.com/essentials/validation#alphanumeric}
     */
    alphanumeric({ name }) {
        /* <i18n case="Shown when the user-provided value contains non-alphanumeric characters."> */
        return `Поле ${sentence(name)} может содержать только буквы и цифры.`;
        /* </i18n> */
    },
    /**
     * The date is not before
     * @see {@link https://docs.formkit.com/essentials/validation#date-before}
     */
    date_before({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date is not before the date supplied to the rule."> */
            return `Дата ${sentence(name)} должна быть раньше ${date(args[0])}.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided date is not before today's date, since no date was supplied to the rule."> */
        return `Дата ${sentence(name)} должна быть в прошлом.`;
        /* </i18n> */
    },
    /**
     * The value is not between two numbers
     * @see {@link https://docs.formkit.com/essentials/validation#between}
     */
    between({ name, args }) {
        if (isNaN(args[0]) || isNaN(args[1])) {
            /* <i18n case="Shown when any of the arguments supplied to the rule were not a number."> */
            return `Это поле заполнено неверно и не может быть отправлено.`;
            /* </i18n> */
        }
        const [a, b] = order(args[0], args[1]);
        /* <i18n case="Shown when the user-provided value is not between two numbers."> */
        return `Поле ${sentence(name)} должно быть между ${a} и ${b}.`;
        /* </i18n> */
    },
    /**
     * The confirmation field does not match
     * @see {@link https://docs.formkit.com/essentials/validation#confirm}
     */
    confirm({ name }) {
        /* <i18n case="Shown when the user-provided value does not equal the value of the matched input."> */
        return `Поле ${sentence(name)} не совпадает.`;
        /* </i18n> */
    },
    /**
     * The value is not a valid date
     * @see {@link https://docs.formkit.com/essentials/validation#date-format}
     */
    date_format({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date does not satisfy the date format supplied to the rule."> */
            return `Поле ${sentence(name)} имеет неверную дату. Пожалуйста, используйте формат ${args[0]}`;
            /* </i18n> */
        }
        /* <i18n case="Shown when no date argument was supplied to the rule."> */
        return 'Это поле заполнено неверно и не может быть отправлено.';
        /* </i18n> */
    },
    /**
     * Is not within expected date range
     * @see {@link https://docs.formkit.com/essentials/validation#date-between}
     */
    date_between({ name, args }) {
        /* <i18n case="Shown when the user-provided date is not between the start and end dates supplied to the rule. "> */
        return `Дата ${sentence(name)} должна быть между ${date(args[0])} и ${date(args[1])}`;
        /* </i18n> */
    },
    /**
     * Shown when the user-provided value is not a valid email address.
     * @see {@link https://docs.formkit.com/essentials/validation#email}
     */
    email: 'Пожалуйста, введите действительный электронный адрес.',
    /**
     * Does not end with the specified value
     * @see {@link https://docs.formkit.com/essentials/validation#ends-with}
     */
    ends_with({ name, args }) {
        /* <i18n case="Shown when the user-provided value does not end with the substring supplied to the rule."> */
        return `Поле ${sentence(name)} не должно заканчиваться на ${list(args)}.`;
        /* </i18n> */
    },
    /**
     * Is not an allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#is}
     */
    is({ name }) {
        /* <i18n case="Shown when the user-provided value is not one of the values supplied to the rule."> */
        return `Поле ${sentence(name)} имеет неподустимое значение.`;
        /* </i18n> */
    },
    /**
     * Does not match specified length
     * @see {@link https://docs.formkit.com/essentials/validation#length}
     */
    length({ name, args: [first = 0, second = Infinity] }) {
        const min = Number(first) <= Number(second) ? first : second;
        const max = Number(second) >= Number(first) ? second : first;
        if (min == 1 && max === Infinity) {
            /* <i18n case="Shown when the length of the user-provided value is not at least one character."> */
            return `Поле ${sentence(name)} должно содержать минимум один символ.`;
            /* </i18n> */
        }
        if (min == 0 && max) {
            /* <i18n case="Shown when first argument supplied to the rule is 0, and the user-provided value is longer than the max (the 2nd argument) supplied to the rule."> */
            return `Длина поля ${sentence(name)} должна быть меньше или равна ${max} символам.`;
            /* </i18n> */
        }
        if (min && max === Infinity) {
            /* <i18n case="Shown when the length of the user-provided value is less than the minimum supplied to the rule and there is no maximum supplied to the rule."> */
            return `Длина поля ${sentence(name)} должна быть больше или равна ${min} символам.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the length of the user-provided value is between the two lengths supplied to the rule."> */
        return `Длина поля ${sentence(name)} должна быть между ${min} и ${max} символами.`;
        /* </i18n> */
    },
    /**
     * Value is not a match
     * @see {@link https://docs.formkit.com/essentials/validation#matches}
     */
    matches({ name }) {
        /* <i18n case="Shown when the user-provided value does not match any of the values or RegExp patterns supplied to the rule. "> */
        return `Поле ${sentence(name)} имеет недопустимое значение.`;
        /* </i18n> */
    },
    /**
     * Exceeds maximum allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#max}
     */
    max({ name, node: { value }, args }) {
        if (Array.isArray(value)) {
            /* <i18n case="Shown when the length of the array of user-provided values is longer than the max supplied to the rule."> */
            return `Не может быть выбрано больше, чем ${args[0]} ${name}.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided value is greater than the maximum number supplied to the rule."> */
        return `Поле ${sentence(name)} должно быть меньше или равно ${args[0]}.`;
        /* </i18n> */
    },
    /**
     * The (field-level) value does not match specified mime type
     * @see {@link https://docs.formkit.com/essentials/validation#mime}
     */
    mime({ name, args }) {
        if (!args[0]) {
            /* <i18n case="Shown when no file formats were supplied to the rule."> */
            return 'Не указаны поддержиавемые форматы файла.';
            /* </i18n> */
        }
        /* <i18n case="Shown when the mime type of user-provided file does not match any mime types supplied to the rule."> */
        return `Формат файла в поле ${sentence(name)} должен быть: ${args[0]}.`;
        /* </i18n> */
    },
    /**
     * Does not fulfill minimum allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#min}
     */
    min({ name, node: { value }, args }) {
        if (Array.isArray(value)) {
            /* <i18n case="Shown when the length of the array of user-provided values is shorter than the min supplied to the rule."> */
            return `Не может быть выбрано меньше, чем ${args[0]} ${name}.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided value is less than the minimum number supplied to the rule."> */
        return `Поле ${sentence(name)} должно быть не менее, чем ${args[0]}.`;
        /* </i18n> */
    },
    /**
     * Is not an allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#not}
     */
    not({ name, node: { value } }) {
        /* <i18n case="Shown when the user-provided value matches one of the values supplied to (and thus disallowed by) the rule."> */
        return `“${value}” не поддерживается в поле ${name}.`;
        /* </i18n> */
    },
    /**
     *  Is not a number
     * @see {@link https://docs.formkit.com/essentials/validation#number}
     */
    number({ name }) {
        /* <i18n case="Shown when the user-provided value is not a number."> */
        return `Поле ${sentence(name)} должно быть числом.`;
        /* </i18n> */
    },
    /**
     * Required field.
     * @see {@link https://docs.formkit.com/essentials/validation#required}
     */
    required({ name }) {
        /* <i18n case="Shown when a user does not provide a value to a required input."> */
        return `Поле ${sentence(name)} обязательно для заполнения.`;
        /* </i18n> */
    },
    /**
     * Does not start with specified value
     * @see {@link https://docs.formkit.com/essentials/validation#starts-with}
     */
    starts_with({ name, args }) {
        /* <i18n case="Shown when the user-provided value does not start with the substring supplied to the rule."> */
        return `Поле ${sentence(name)} должно начинаться с ${list(args)}.`;
        /* </i18n> */
    },
    /**
     * Is not a url
     * @see {@link https://docs.formkit.com/essentials/validation#url}
     */
    url() {
        /* <i18n case="Shown when the user-provided value is not a valid url."> */
        return `Пожалуйста, введите действительный URL адрес.`;
        /* </i18n> */
    },
};

var ru = /*#__PURE__*/Object.freeze({
    __proto__: null,
    ui: ui$2,
    validation: validation$2
});

/**
 * Here we can import additional helper functions to assist in formatting our
 * language. Feel free to add additional helper methods to libs/formats if it
 * assists in creating good validation messages for your locale.
 */
/**
 * Standard language for interface features.
 * @public
 */
const ui$1 = {
    /**
     * Shown when a button to remove items is visible.
     */
    remove: 'Kaldır',
    /**
     * Shown when there are multiple items to remove at the same time.
     */
    removeAll: 'Hepsini kaldır',
    /**
     * Shown when all fields are not filled out correctly.
     */
    incomplete: 'Maalesef, tüm alanlar doğru doldurulmadı.',
    /**
     * Shown in a button inside a form to submit the form.
     */
    submit: 'Gönder',
    /**
     * Shown when no files are selected.
     */
    noFiles: 'Dosya yok',
};
/**
 * These are all the possible strings that pertain to validation messages.
 * @public
 */
const validation$1 = {
    /**
     * The value is not an accepted value.
     * @see {@link https://docs.formkit.com/essentials/validation#accepted}
     */
    accepted({ name }) {
        /* <i18n case="Shown when the user-provided value is not a valid 'accepted' value."> */
        return `Lütfen ${name}'yi kabul edin.`;
        /* </i18n> */
    },
    /**
     * The date is not after
     * @see {@link https://docs.formkit.com/essentials/validation#date-after}
     */
    date_after({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date is not after the date supplied to the rule."> */
            return `${sentence(name)} ${date(args[0])}'den sonra olmalıdır.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided date is not after today's date, since no date was supplied to the rule."> */
        return `${sentence(name)} gelecekte bir zaman olmalıdır.`;
        /* </i18n> */
    },
    /**
     * The value is not a letter.
     * @see {@link https://docs.formkit.com/essentials/validation#alpha}
     */
    alpha({ name }) {
        /* <i18n case="Shown when the user-provided value contains non-alphabetical characters."> */
        return `${sentence(name)} sadece alfabetik karakterler içerebilir.`;
        /* </i18n> */
    },
    /**
     * The value is not alphanumeric
     * @see {@link https://docs.formkit.com/essentials/validation#alphanumeric}
     */
    alphanumeric({ name }) {
        /* <i18n case="Shown when the user-provided value contains non-alphanumeric characters."> */
        return `${sentence(name)} sadece alfabetik karakterler ve sayı içerebilir.`;
        /* </i18n> */
    },
    /**
     * The date is not before
     * @see {@link https://docs.formkit.com/essentials/validation#date-before}
     */
    date_before({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date is not before the date supplied to the rule."> */
            return `${sentence(name)} ${date(args[0])} tarihinden önce olmalı.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided date is not before today's date, since no date was supplied to the rule."> */
        return `${sentence(name)} geçmişte olmalı.`;
        /* </i18n> */
    },
    /**
     * The value is not between two numbers
     * @see {@link https://docs.formkit.com/essentials/validation#between}
     */
    between({ name, args }) {
        if (isNaN(args[0]) || isNaN(args[1])) {
            /* <i18n case="Shown when any of the arguments supplied to the rule were not a number."> */
            return `Alan yanlış yapılandırılmış ve gönderilemez.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided value is not between two numbers."> */
        return `${sentence(name)} ${args[0]} ve ${args[1]} aralığında olmalı.`;
        /* </i18n> */
    },
    /**
     * The confirmation field does not match
     * @see {@link https://docs.formkit.com/essentials/validation#confirm}
     */
    confirm({ name }) {
        /* <i18n case="Shown when the user-provided value does not equal the value of the matched input."> */
        return `${sentence(name)} eşleşmiyor.`;
        /* </i18n> */
    },
    /**
     * The value is not a valid date
     * @see {@link https://docs.formkit.com/essentials/validation#date-format}
     */
    date_format({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date does not satisfy the date format supplied to the rule."> */
            return `${sentence(name)} geçerli bir tarih değil, lütfen ${args[0]} biçimini kullanın.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when no date argument was supplied to the rule."> */
        return 'Alan yanlış yapılandırılmış ve gönderilemez.';
        /* </i18n> */
    },
    /**
     * Is not within expected date range
     * @see {@link https://docs.formkit.com/essentials/validation#date-between}
     */
    date_between({ name, args }) {
        /* <i18n case="Shown when the user-provided date is not between the start and end dates supplied to the rule. "> */
        return `${sentence(name)}, ${date(args[0])} ve ${date(args[1])} aralığında olmalı.`;
        /* </i18n> */
    },
    /**
     * Shown when the user-provided value is not a valid email address.
     * @see {@link https://docs.formkit.com/essentials/validation#email}
     */
    email: 'Lütfen geçerli bir e-mail adresi girin.',
    /**
     * Does not end with the specified value
     * @see {@link https://docs.formkit.com/essentials/validation#ends-with}
     */
    ends_with({ name, args }) {
        /* <i18n case="Shown when the user-provided value does not end with the substring supplied to the rule."> */
        return `${sentence(name)} ${list(args)} ile bitmiyor.`;
        /* </i18n> */
    },
    /**
     * Is not an allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#is}
     */
    is({ name }) {
        /* <i18n case="Shown when the user-provided value is not one of the values supplied to the rule."> */
        return `${sentence(name)} izin verilen bir değer değil.`;
        /* </i18n> */
    },
    /**
     * Does not match specified length
     * @see {@link https://docs.formkit.com/essentials/validation#length}
     */
    length({ name, args: [first = 0, second = Infinity] }) {
        const min = first <= second ? first : second;
        const max = second >= first ? second : first;
        if (min == 1 && max === Infinity) {
            /* <i18n case="Shown when the length of the user-provided value is not at least one character."> */
            return `${sentence(name)} en azından bir karakter uzunluğunda olmalı.`;
            /* </i18n> */
        }
        if (min == 0 && max) {
            /* <i18n case="Shown when first argument supplied to the rule is 0, and the user-provided value is longer than the max (the 2nd argument) supplied to the rule."> */
            return `${sentence(name)} ${max}'e eşit veya daha küçük olmalı.`;
            /* </i18n> */
        }
        if (min && max === Infinity) {
            /* <i18n case="Shown when the length of the user-provided value is less than the minimum supplied to the rule and there is no maximum supplied to the rule."> */
            return `${sentence(name)} ${min}'e eşit veya daha büyük olmalı.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the length of the user-provided value is between the two lengths supplied to the rule."> */
        return `${sentence(name)}, ${min} ve ${max} karakter uzunluğu aralığında olmalı.`;
        /* </i18n> */
    },
    /**
     * Value is not a match
     * @see {@link https://docs.formkit.com/essentials/validation#matches}
     */
    matches({ name }) {
        /* <i18n case="Shown when the user-provided value does not match any of the values or RegExp patterns supplied to the rule. "> */
        return `${sentence(name)} izin verilen bir değer değil.`;
        /* </i18n> */
    },
    /**
     * Exceeds maximum allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#max}
     */
    max({ name, node: { value }, args }) {
        if (Array.isArray(value)) {
            /* <i18n case="Shown when the length of the array of user-provided values is longer than the max supplied to the rule."> */
            return `${name}'in uzunluğu ${args[0]}'dan daha uzun olamaz.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided value is greater than the maximum number supplied to the rule."> */
        return `${sentence(name)} en azından ${args[0]} uzunluğunda veya ona eşit olmalı.`;
        /* </i18n> */
    },
    /**
     * The (field-level) value does not match specified mime type
     * @see {@link https://docs.formkit.com/essentials/validation#mime}
     */
    mime({ name, args }) {
        if (!args[0]) {
            /* <i18n case="Shown when no file formats were supplied to the rule."> */
            return 'Hiçbir dosya türüne izin verilmez.';
            /* </i18n> */
        }
        /* <i18n case="Shown when the mime type of user-provided file does not match any mime types supplied to the rule."> */
        return `${sentence(name)} şu tiplerden biri olmalı: ${args[0]}`;
        /* </i18n> */
    },
    /**
     * Does not fulfill minimum allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#min}
     */
    min({ name, node: { value }, args }) {
        if (Array.isArray(value)) {
            /* <i18n case="Shown when the length of the array of user-provided values is shorter than the min supplied to the rule."> */
            return `${name}'in uzunluğu ${args[0]}'dan daha kısa olamaz.`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided value is less than the minimum number supplied to the rule."> */
        return `${sentence(name)} en azından ${args[0]} uzunluğunda olmalı.`;
        /* </i18n> */
    },
    /**
     * Is not an allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#not}
     */
    not({ name, node: { value } }) {
        /* <i18n case="Shown when the user-provided value matches one of the values supplied to (and thus disallowed by) the rule."> */
        return `“${value}” ${name} olamaz.`;
        /* </i18n> */
    },
    /**
     *  Is not a number
     * @see {@link https://docs.formkit.com/essentials/validation#number}
     */
    number({ name }) {
        /* <i18n case="Shown when the user-provided value is not a number."> */
        return `${sentence(name)} sayı olmalı.`;
        /* </i18n> */
    },
    /**
     * Required field.
     * @see {@link https://docs.formkit.com/essentials/validation#required}
     */
    required({ name }) {
        /* <i18n case="Shown when a user does not provide a value to a required input."> */
        return `${sentence(name)} gerekli.`;
        /* </i18n> */
    },
    /**
     * Does not start with specified value
     * @see {@link https://docs.formkit.com/essentials/validation#starts-with}
     */
    starts_with({ name, args }) {
        /* <i18n case="Shown when the user-provided value does not start with the substring supplied to the rule."> */
        return `${sentence(name)} ${list(args)} ile başlamıyor.`;
        /* </i18n> */
    },
    /**
     * Is not a url
     * @see {@link https://docs.formkit.com/essentials/validation#url}
     */
    url() {
        /* <i18n case="Shown when the user-provided value is not a valid url."> */
        return `Lütfen geçerli bir url dahil edin.`;
        /* </i18n> */
    },
};

var tr = /*#__PURE__*/Object.freeze({
    __proto__: null,
    ui: ui$1,
    validation: validation$1
});

/**
 * Here we can import additional helper functions to assist in formatting our
 * language. Feel free to add additional helper methods to libs/formats if it
 * assists in creating good validation messages for your locale.
 */
/**
 * Standard language for interface features.
 * @public
 */
const ui = {
    /**
     * Shown when a button to remove items is visible.
     */
    remove: '移除',
    /**
     * Shown when all fields are not filled out correctly.
     */
    incomplete: '对不起，有些字段未被正确填写',
    /**
     * Shown in a button inside a form to submit the form.
     */
    submit: '提交',
};
/**
 * These are all the possible strings that pertain to validation messages.
 * @public
 */
const validation = {
    /**
     * The value is not an accepted value.
     * @see {@link https://docs.formkit.com/essentials/validation#accepted}
     */
    accepted({ name }) {
        /* <i18n case="Shown when the user-provided value is not a valid 'accepted' value."> */
        return `请接受${name}`;
        /* </i18n> */
    },
    /**
     * The date is not after
     * @see {@link https://docs.formkit.com/essentials/validation#date-after}
     */
    date_after({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date is not after the date supplied to the rule."> */
            return `${sentence(name)}必须晚于${date(args[0])}`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided date is not after today's date, since no date was supplied to the rule."> */
        return `${sentence(name)}必须是未来的日期`;
        /* </i18n> */
    },
    /**
     * The value is not a letter.
     * @see {@link https://docs.formkit.com/essentials/validation#alpha}
     */
    alpha({ name }) {
        /* <i18n case="Shown when the user-provided value contains non-alphabetical characters."> */
        return `${sentence(name)}仅能包含字母字符`;
        /* </i18n> */
    },
    /**
     * The value is not alphanumeric
     * @see {@link https://docs.formkit.com/essentials/validation#alphanumeric}
     */
    alphanumeric({ name }) {
        /* <i18n case="Shown when the user-provided value contains non-alphanumeric characters."> */
        return `${sentence(name)}仅能包含字母和数字`;
        /* </i18n> */
    },
    /**
     * The date is not before
     * @see {@link https://docs.formkit.com/essentials/validation#date-before}
     */
    date_before({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date is not before the date supplied to the rule."> */
            return `${sentence(name)}必须早于${date(args[0])}`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided date is not before today's date, since no date was supplied to the rule."> */
        return `${sentence(name)}必须是过去的日期`;
        /* </i18n> */
    },
    /**
     * The value is not between two numbers
     * @see {@link https://docs.formkit.com/essentials/validation#between}
     */
    between({ name, args }) {
        if (isNaN(args[0]) || isNaN(args[1])) {
            /* <i18n case="Shown when any of the arguments supplied to the rule were not a number."> */
            return `该字段未被正确设置而无法被提交`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided value is not between two numbers."> */
        return `${sentence(name)}必须在${args[0]}和${args[1]}之间`;
        /* </i18n> */
    },
    /**
     * The confirmation field does not match
     * @see {@link https://docs.formkit.com/essentials/validation#confirm}
     */
    confirm({ name }) {
        /* <i18n case="Shown when the user-provided value does not equal the value of the matched input."> */
        return `${sentence(name)}不匹配`;
        /* </i18n> */
    },
    /**
     * The value is not a valid date
     * @see {@link https://docs.formkit.com/essentials/validation#date-format}
     */
    date_format({ name, args }) {
        if (Array.isArray(args) && args.length) {
            /* <i18n case="Shown when the user-provided date does not satisfy the date format supplied to the rule."> */
            return `${sentence(name)}不是一个合法日期，请使用此格式${args[0]}`;
            /* </i18n> */
        }
        /* <i18n case="Shown when no date argument was supplied to the rule."> */
        return '该字段未被正确设置而无法被提交';
        /* </i18n> */
    },
    /**
     * Is not within expected date range
     * @see {@link https://docs.formkit.com/essentials/validation#date-between}
     */
    date_between({ name, args }) {
        /* <i18n case="Shown when the user-provided date is not between the start and end dates supplied to the rule. "> */
        return `${sentence(name)}必须在${date(args[0])}和${date(args[1])}之间`;
        /* </i18n> */
    },
    /**
     * Shown when the user-provided value is not a valid email address.
     * @see {@link https://docs.formkit.com/essentials/validation#email}
     */
    email: '请输入一个合法的电子邮件地址',
    /**
     * Does not end with the specified value
     * @see {@link https://docs.formkit.com/essentials/validation#ends-with}
     */
    ends_with({ name, args }) {
        /* <i18n case="Shown when the user-provided value does not end with the substring supplied to the rule."> */
        return `${sentence(name)}并未以${list(args)}结尾`;
        /* </i18n> */
    },
    /**
     * Is not an allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#is}
     */
    is({ name }) {
        /* <i18n case="Shown when the user-provided value is not one of the values supplied to the rule."> */
        return `${sentence(name)}不是一个允许值`;
        /* </i18n> */
    },
    /**
     * Does not match specified length
     * @see {@link https://docs.formkit.com/essentials/validation#length}
     */
    length({ name, args: [first = 0, second = Infinity] }) {
        const min = first <= second ? first : second;
        const max = second >= first ? second : first;
        if (min == 1 && max === Infinity) {
            /* <i18n case="Shown when the length of the user-provided value is not at least one character."> */
            return `${sentence(name)}至少要有一个字符`;
            /* </i18n> */
        }
        if (min == 0 && max) {
            /* <i18n case="Shown when first argument supplied to the rule is 0, and the user-provided value is longer than the max (the 2nd argument) supplied to the rule."> */
            return `${sentence(name)}必须少于或等于${max}个字符`;
            /* </i18n> */
        }
        if (min && max === Infinity) {
            /* <i18n case="Shown when the length of the user-provided value is less than the minimum supplied to the rule and there is no maximum supplied to the rule."> */
            return `${sentence(name)}必须多于或等于${min}个字符`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the length of the user-provided value is between the two lengths supplied to the rule."> */
        return `${sentence(name)}必须有${min}至${max}个字符`;
        /* </i18n> */
    },
    /**
     * Value is not a match
     * @see {@link https://docs.formkit.com/essentials/validation#matches}
     */
    matches({ name }) {
        /* <i18n case="Shown when the user-provided value does not match any of the values or RegExp patterns supplied to the rule. "> */
        return `${sentence(name)}不是一个允许值`;
        /* </i18n> */
    },
    /**
     * Exceeds maximum allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#max}
     */
    max({ name, node: { value }, args }) {
        if (Array.isArray(value)) {
            /* <i18n case="Shown when the length of the array of user-provided values is longer than the max supplied to the rule."> */
            return `${name}不能超过${args[0]}`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided value is greater than the maximum number supplied to the rule."> */
        return `${sentence(name)}必须小于或等于${args[0]}`;
        /* </i18n> */
    },
    /**
     * The (field-level) value does not match specified mime type
     * @see {@link https://docs.formkit.com/essentials/validation#mime}
     */
    mime({ name, args }) {
        if (!args[0]) {
            /* <i18n case="Shown when no file formats were supplied to the rule."> */
            return '无允许的文件格式';
            /* </i18n> */
        }
        /* <i18n case="Shown when the mime type of user-provided file does not match any mime types supplied to the rule."> */
        return `${sentence(name)}必须为此类型：${args[0]}`;
        /* </i18n> */
    },
    /**
     * Does not fulfill minimum allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#min}
     */
    min({ name, node: { value }, args }) {
        if (Array.isArray(value)) {
            /* <i18n case="Shown when the length of the array of user-provided values is shorter than the min supplied to the rule."> */
            return `${name}不能少于${args[0]}`;
            /* </i18n> */
        }
        /* <i18n case="Shown when the user-provided value is less than the minimum number supplied to the rule."> */
        return `${sentence(name)}不能小于${args[0]}`;
        /* </i18n> */
    },
    /**
     * Is not an allowed value
     * @see {@link https://docs.formkit.com/essentials/validation#not}
     */
    not({ name, node: { value } }) {
        /* <i18n case="Shown when the user-provided value matches one of the values supplied to (and thus disallowed by) the rule."> */
        return `“${value}”不是一个被允许的${name}`;
        /* </i18n> */
    },
    /**
     *  Is not a number
     * @see {@link https://docs.formkit.com/essentials/validation#number}
     */
    number({ name }) {
        /* <i18n case="Shown when the user-provided value is not a number."> */
        return `${sentence(name)}必须是一个数字`;
        /* </i18n> */
    },
    /**
     * Required field.
     * @see {@link https://docs.formkit.com/essentials/validation#required}
     */
    required({ name }) {
        /* <i18n case="Shown when a user does not provide a value to a required input."> */
        return `${sentence(name)}必须填写`;
        /* </i18n> */
    },
    /**
     * Does not start with specified value
     * @see {@link https://docs.formkit.com/essentials/validation#starts-with}
     */
    starts_with({ name, args }) {
        /* <i18n case="Shown when the user-provided value does not start with the substring supplied to the rule."> */
        return `${sentence(name)}没有以${list(args)}开头`;
        /* </i18n> */
    },
    /**
     * Is not a url
     * @see {@link https://docs.formkit.com/essentials/validation#url}
     */
    url() {
        /* <i18n case="Shown when the user-provided value is not a valid url."> */
        return `请包含一个合法的url`;
        /* </i18n> */
    },
};

var zh = /*#__PURE__*/Object.freeze({
    __proto__: null,
    ui: ui,
    validation: validation
});

/**
 * Create a new internationalization plugin for FormKit.
 * @param locales - Creates the i18n plugin.
 * @public
 */
function createI18nPlugin(registry) {
    return function i18nPlugin(node) {
        let localeKey = parseLocale(node.config.locale, registry);
        let locale = localeKey ? registry[localeKey] : {};
        /* If the locale prop changes, update the active locale */
        node.on('prop:locale', ({ payload: lang }) => {
            localeKey = parseLocale(lang, registry);
            locale = localeKey ? registry[localeKey] : {};
            // Run through all the messages in the store and update with new locale
            node.store.touch();
        });
        /**
         * Hook into the core text or t() hook to perform localization on the
         * output of core functionality.
         */
        node.hook.text((fragment, next) => {
            var _a, _b;
            const key = ((_a = fragment.meta) === null || _a === void 0 ? void 0 : _a.messageKey) || fragment.key;
            if (has(locale, fragment.type) && has(locale[fragment.type], key)) {
                const t = locale[fragment.type][key];
                if (typeof t === 'function') {
                    fragment.value = Array.isArray((_b = fragment.meta) === null || _b === void 0 ? void 0 : _b.i18nArgs)
                        ? t(...fragment.meta.i18nArgs) // eslint-disable-line @typescript-eslint/no-non-null-assertion
                        : t(fragment);
                }
                else {
                    fragment.value = t;
                }
            }
            return next(fragment);
        });
    };
}
/**
 * @param locale - An ISO 639-1 and (optionally) ISO 639-2 language tag. For
 * example these are valid locale keys:
 * zh
 * zh-CN
 * zh-HK
 * en
 * en-GB
 * @param availableLocales - An array of locales that may be valid.
 */
function parseLocale(locale, availableLocales) {
    if (has(availableLocales, locale)) {
        return locale;
    }
    const [lang] = locale.split('-');
    if (has(availableLocales, lang)) {
        return lang;
    }
    for (const locale in availableLocales) {
        return locale;
    }
    return false;
}

/**
 * Export all the available locales at once.
 * @public
 */
const locales = {
    de,
    en,
    es,
    fa,
    fi,
    fr,
    he,
    hr,
    nl,
    pt,
    ru,
    tr,
    zh,
};

export { createI18nPlugin, date, de, en, es, fa, fi, fr, he, hr, list, locales, nl, order, pt, ru, sentence, tr, zh };
